/* eslint-disable default-case */
import React, { useState, createRef, useEffect, useRef, useMemo, memo } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import ToggleBtnType from "Components/common/ToggleBtnType";
import {
  filterComponentData,
  companyFilterData,
  contactFilterData,
  DEFAULT_EXPERIENCE_DURATION
} from "./filterComponentData";
import { dataSearchState } from "../state";
import InputBox from "Components/Register/InputBox";
import { AddRemoveValue } from "./AddRemoveValue";
import { CustomMultiSelect } from "./CustomMultiSelect";
import FilterPanelOverlay from "./FilterPanelOverlay";
import FilterPanelCheckboxList from "./FilterPanelCheckboxList";
import { AutoCompleteMultiSelectDropdown } from "./AutoCompleteMultiSelectDropdown";
import CustomDropdownWithSubmenu from "./CustomDropdownWithSubmenu";
import JobFunctionDropdown from "./JobFunctionDropdown";
import FundingDropdown from "./FundingDropdown";
import { FUNDING_CATEGORIES } from "./fundingData";
import { JOB_FUNCTION_CATEGORIES } from "./jobFunctionData";
import EmployeesByDepartmentInputs from "./EmployeesByDepartmentInputs";
import GrowthByDepartmentInputs from "./GrowthByDepartmentInputs";
import FoundedYearInputs from "./FoundedYearInputs";
import ArrayUtils from "Utils/ArrayUtils";
import Utils from "Utils/utils";
import {
  verifyBulkActionPerformedAndAllow,
  isValidDomain,
  normalizeLinkedInCompanyUrl,
  normalizeLinkedInProfileUrl
} from "../DataSearchUtils";
import { MXP_EVENT, PAGE_TYPE } from "Static/MixPanelEvents";
import makeApi, { URLS } from "Utils/apiURL";
import AutoCompleteDropdown from "Components/common/AutoCompleteDropdown";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { toasterState } from "Components/common/toaster";
import Tooltip from "Components/common/Tooltip";
import { ReactComponent as EditIcon } from "Assets/svg/icn-rename1.svg";
import { ReactComponent as DeleteIconDiscover } from "Assets/svg/icn-delete1.svg";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const MAX_COMPANY_DOMAIN_NAME_ITEMS = 5;
const MAX_CSV_LIST_RECORDS = 100;
const MAX_EXCLUDE_CONTACT_URLS = 300;
const CSV_RULES_TOOLTIP = "One column, no header. Max 100 records.";

/** Short hints shown right below each filter field; hidden when the field has a value or search results are shown. */
const FILTER_FIELD_HELP = {
  title: { description: "Search by job title or role.", example: "e.g. VP Sales, Marketing Manager" },
  companyDomain: { description: "Filter by company domain, name, or LinkedIn company URL.", example: "e.g. acme.com, Acme Inc, or linkedin.com/company/acme" },
  companyLocation: { description: "Filter by city, state, or country where the company is headquartered.", example: "e.g. San Francisco, California" },
  foundedYear: { description: "Limit to companies founded in a year or range.", example: "e.g. 2010 to 2020" },
  companyKeywords: { description: "Match companies by keywords that describe them.", example: "e.g. SaaS, enterprise" },
  contactKeywords: { description: "Match contacts whose profiles mention these terms.", example: "e.g. sales, growth" },
  skill: { description: "Match contacts by skills listed on their profile.", example: "e.g. leadership, Python" },
  certification: { description: "Match contacts by certifications they hold.", example: "e.g. Scrum Master Certification (CSM)" },
  name: {
    description: "Choose Contact name or LinkedIn profile URL (one at a time), then add values.",
    example: "e.g. Jane Doe, or linkedin.com/in/jane-doe when LinkedIn is selected"
  },
  excludeContacts: {
    description: "Exclude contacts by one saved list or pasted LinkedIn profile URLs.",
    example: "Uses top 300 LinkedIn profiles for exclusion."
  },
  location: { description: "Filter by where the contact is located.", example: "e.g. New York, United States" }
};

const parseCsvToOneColumn = text => {
  const lines = String(text || "").split(/\r?\n/).map(l => {
    const firstCol = l.trim().split(",")[0];
    return firstCol ? firstCol.trim() : "";
  }).filter(Boolean);
  return lines;
};

const CompanyDomainNameInputs = observer(({ inputBoxRef }) => {
  const [domainValue, setDomainValue] = useState("");
  const [nameValue, setNameValue] = useState("");
  const [domainError, setDomainError] = useState(false);
  // pastePopup: null | "domainList" | "nameList" | "linkedinList"
  const [pastePopup, setPastePopup] = useState(null);
  const [pasteValue, setPasteValue] = useState("");
  const pasteTextareaRef = useRef(null);
  const [linkedinValue, setLinkedinValue] = useState("");
  const [linkedinError, setLinkedinError] = useState(false);

  const searchType = dataSearchState?.filterComponentData?.companySearchType || "domain";
  const companyDomain = dataSearchState?.filterComponentData?.companyDomain || [];
  const companyName = dataSearchState?.filterComponentData?.companyName || [];
  const companyDomainList = dataSearchState?.filterComponentData?.companyDomainList || [];
  const companyNameList = dataSearchState?.filterComponentData?.companyNameList || [];
  const companyLinkedIn = dataSearchState?.filterComponentData?.companyLinkedIn || [];
  const companyLinkedInList = dataSearchState?.filterComponentData?.companyLinkedInList || [];

  const setSearchType = val => {
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      companySearchType: val
    });
  };

  const openPastePopup = type => {
    setSearchType(type);
    const existing =
      type === "domainList"
        ? companyDomainList
        : type === "linkedinList"
          ? companyLinkedInList
          : companyNameList;
    setPasteValue(existing.join("\n"));
    setPastePopup(type);
    setTimeout(() => pasteTextareaRef.current?.focus(), 50);
  };

  const closePastePopup = () => {
    setPastePopup(null);
    setPasteValue("");
  };

  // Parse pasted text: split on newlines and/or commas, trim, deduplicate
  const parsePastedText = text =>
    [...new Set(
      String(text || "")
        .split(/[\n,]+/)
        .map(s => s.trim())
        .filter(Boolean)
    )];

  const applyPastedList = () => {
    const items = parsePastedText(pasteValue);
    if (items.length > MAX_CSV_LIST_RECORDS) {
      toasterState.setToastMsg(`Maximum ${MAX_CSV_LIST_RECORDS} entries allowed.`, "fail");
      return;
    }
    if (pastePopup === "domainList") {
      const valid = items.filter(line => isValidDomain(line));
      if (valid.length === 0 && items.length > 0) {
        toasterState.setToastMsg("No valid domains found. Use format: example.com", "fail");
        return;
      }
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        companyDomainList: valid,
        companySearchType: "domainList"
      });
    } else if (pastePopup === "linkedinList") {
      const normalized = [...new Set(items.map(line => normalizeLinkedInCompanyUrl(line)).filter(Boolean))];
      if (normalized.length === 0 && items.length > 0) {
        toasterState.setToastMsg(
          "No valid LinkedIn company URLs. Use links containing linkedin.com/company/…",
          "fail"
        );
        return;
      }
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        companyLinkedInList: normalized,
        companySearchType: "linkedinList"
      });
    } else {
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        companyNameList: items,
        companySearchType: "nameList"
      });
    }
    closePastePopup();
  };

  const addDomain = () => {
    const raw = domainValue.trim();
    if (!raw) return;
    const tokens = raw.split(",").map(t => t.trim()).filter(Boolean);
    const invalid = tokens.find(t => !isValidDomain(t));
    if (invalid) {
      setDomainError(true);
      return;
    }
    setDomainError(false);
    const newTokens = tokens.filter(t => !companyDomain.includes(t));
    const combined = [...companyDomain, ...newTokens].slice(0, MAX_COMPANY_DOMAIN_NAME_ITEMS);
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      companyDomain: combined
    });
    setDomainValue("");
  };

  const addName = () => {
    const v = nameValue.trim();
    if (!v) return;
    if (companyName.length >= MAX_COMPANY_DOMAIN_NAME_ITEMS) return;
    if (companyName.length > 0 && companyName.includes(v)) {
      setNameValue("");
      return;
    }
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      companyName: [...companyName, v]
    });
    setNameValue("");
  };

  const addLinkedIn = () => {
    const v = linkedinValue.trim();
    if (!v) return;
    if (companyLinkedIn.length >= MAX_COMPANY_DOMAIN_NAME_ITEMS) return;
    const normalized = normalizeLinkedInCompanyUrl(v);
    if (!normalized) {
      setLinkedinError(true);
      return;
    }
    if (companyLinkedIn.some(u => normalizeLinkedInCompanyUrl(u) === normalized)) {
      setLinkedinValue("");
      return;
    }
    setLinkedinError(false);
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      companyLinkedIn: [...companyLinkedIn, normalized]
    });
    setLinkedinValue("");
  };

  return (
    <div className="companyDomainNameSection">
      <div className="filterComponentInputHeader companyDomainNameInputsStack">
        <div className={`companyDomainNameInputWrap ${domainError ? "companyDomainNameInputWrap--error" : ""}`}>
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "domain"}
              onChange={() => setSearchType("domain")}
            />
            <span>Company Domain</span>
          </label>
          <InputBox
            name="companyDomainInput"
            id="companyDomainInput"
            type="text"
            value={domainValue}
            changeAction={(val) => { setDomainValue(val); setDomainError(false); }}
            keyupAction={e => Utils.isSubmitKeyAction(e) && addDomain()}
            blurAction={() => addDomain()}
            label=""
            text=""
            placeholder="e.g. stripe.com, acme.com"
            inputBoxRef={inputBoxRef}
            focus={true}
            readOnly={searchType !== "domain"}
          />
          {searchType === "domain" && domainValue.trim() && <p className="filter-input-enter-hint">Press Enter to add</p>}
        </div>
        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "name"}
              onChange={() => setSearchType("name")}
            />
            <span>Company Name</span>
          </label>
          <InputBox
            name="companyNameInput"
            id="companyNameInput"
            type="text"
            value={nameValue}
            changeAction={(val) => setNameValue(val)}
            keyupAction={e => Utils.isSubmitKeyAction(e) && addName()}
            blurAction={() => addName()}
            label=""
            text=""
            placeholder="e.g. Acme Inc"
            readOnly={searchType !== "name"}
          />
          {searchType === "name" && nameValue.trim() && <p className="filter-input-enter-hint">Press Enter to add</p>}
        </div>
        <div className={`companyDomainNameInputWrap ${linkedinError ? "companyDomainNameInputWrap--error" : ""}`}>
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "linkedin"}
              onChange={() => setSearchType("linkedin")}
            />
            <span>LinkedIn URL</span>
          </label>
          <InputBox
            name="companyLinkedInInput"
            id="companyLinkedInInput"
            type="text"
            value={linkedinValue}
            changeAction={val => {
              setLinkedinValue(val);
              setLinkedinError(false);
            }}
            keyupAction={e => Utils.isSubmitKeyAction(e) && addLinkedIn()}
            blurAction={() => addLinkedIn()}
            label=""
            text=""
            placeholder="e.g. linkedin.com/company/acme"
            readOnly={searchType !== "linkedin"}
          />
          {searchType === "linkedin" && linkedinValue.trim() && (
            <p className="filter-input-enter-hint">Press Enter to add</p>
          )}
        </div>
        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "domainList"}
              onChange={() => setSearchType("domainList")}
            />
            <span>Company Domain List</span>
          </label>
          <div className="companyDomainNamePasteRow">
            <button
              type="button"
              className={`companyDomainNamePasteBtn${searchType === "domainList" && companyDomainList.length > 0 ? " companyDomainNamePasteBtn--hasValue" : ""}`}
              onClick={() => openPastePopup("domainList")}
            >
              <i className="material-icons">content_paste</i>
              {searchType === "domainList" && companyDomainList.length > 0
                ? `${companyDomainList.length} domain${companyDomainList.length !== 1 ? "s" : ""} added`
                : "Add Domains"}
            </button>
          </div>
        </div>
        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "nameList"}
              onChange={() => setSearchType("nameList")}
            />
            <span>Company Name List</span>
          </label>
          <div className="companyDomainNamePasteRow">
            <button
              type="button"
              className={`companyDomainNamePasteBtn${searchType === "nameList" && companyNameList.length > 0 ? " companyDomainNamePasteBtn--hasValue" : ""}`}
              onClick={() => openPastePopup("nameList")}
            >
              <i className="material-icons">content_paste</i>
              {searchType === "nameList" && companyNameList.length > 0
                ? `${companyNameList.length} name${companyNameList.length !== 1 ? "s" : ""} added`
                : "Add Names"}
            </button>
          </div>
        </div>
        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "linkedinList"}
              onChange={() => setSearchType("linkedinList")}
            />
            <span>LinkedIn URL List</span>
          </label>
          <div className="companyDomainNamePasteRow">
            <button
              type="button"
              className={`companyDomainNamePasteBtn${searchType === "linkedinList" && companyLinkedInList.length > 0 ? " companyDomainNamePasteBtn--hasValue" : ""}`}
              onClick={() => openPastePopup("linkedinList")}
            >
              <i className="material-icons">content_paste</i>
              {searchType === "linkedinList" && companyLinkedInList.length > 0
                ? `${companyLinkedInList.length} URL${companyLinkedInList.length !== 1 ? "s" : ""} added`
                : "Add LinkedIn URLs"}
            </button>
          </div>
        </div>
      </div>

      {/* Paste popup — rendered into document.body via portal so position:fixed is relative to viewport */}
      {pastePopup && ReactDOM.createPortal(
        <div className="cdnPasteOverlay" onClick={closePastePopup}>
          <div className="cdnPastePopup" onClick={e => e.stopPropagation()}>
            <div className="cdnPastePopup__header">
              <span className="cdnPastePopup__title">
                {pastePopup === "domainList"
                  ? "Add Company Domains"
                  : pastePopup === "linkedinList"
                    ? "Add LinkedIn Company URLs"
                    : "Add Company Names"}
              </span>
              <button type="button" className="cdnPastePopup__close" onClick={closePastePopup}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <p className="cdnPastePopup__hint">
              {pastePopup === "domainList"
                ? "Paste or type one domain per line (e.g. example.com). Commas also work. Max 100."
                : pastePopup === "linkedinList"
                  ? "One LinkedIn company URL per line (must include /company/). Commas also work. Max 100."
                  : "Paste or type one company name per line. Commas also work. Max 100."}
            </p>
            <textarea
              ref={pasteTextareaRef}
              className="cdnPastePopup__textarea"
              placeholder={
                pastePopup === "domainList"
                  ? "example.com\nacme.io\ncontoso.com"
                  : pastePopup === "linkedinList"
                    ? "https://www.linkedin.com/company/acme\nlinkedin.com/company/contoso"
                    : "Acme Inc\nContoso\nGlobex"
              }
              value={pasteValue}
              onChange={e => setPasteValue(e.target.value)}
            />
            <div className="cdnPastePopup__footer">
              <span className="cdnPastePopup__count">
                {(() => {
                  const items = parsePastedText(pasteValue);
                  if (!items.length) return "";
                  if (pastePopup === "domainList") {
                    const valid = items.filter(isValidDomain).length;
                    return `${valid} valid domain${valid !== 1 ? "s" : ""} of ${items.length} entered`;
                  }
                  if (pastePopup === "linkedinList") {
                    const valid = items.filter(line => normalizeLinkedInCompanyUrl(line)).length;
                    return `${valid} valid URL${valid !== 1 ? "s" : ""} of ${items.length} entered`;
                  }
                  return `${items.length} name${items.length !== 1 ? "s" : ""} entered`;
                })()}
              </span>
              <div className="cdnPastePopup__actions">
                <button type="button" className="cdnPastePopup__cancel" onClick={closePastePopup}>Cancel</button>
                <button type="button" className="cdnPastePopup__apply" onClick={applyPastedList}>Apply</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

const ContactInfoInputs = observer(({ inputBoxRef }) => {
  const [nameInput, setNameInput] = useState("");
  const [linkedinInput, setLinkedinInput] = useState("");
  const [linkedinError, setLinkedinError] = useState(false);

  const contactSearchType = dataSearchState?.filterComponentData?.contactSearchType || "name";
  const fd = dataSearchState?.filterComponentData || {};

  const setSearchType = val => {
    if (val === contactSearchType) return;
    if (val === "name") {
      dataSearchState.setFilterComponentData({
        ...fd,
        contactSearchType: "name",
        contactLinkedIn: []
      });
    } else {
      dataSearchState.setFilterComponentData({
        ...fd,
        contactSearchType: "linkedin",
        name: []
      });
    }
  };

  const rawName = dataSearchState?.filterComponentData?.name;
  const names = Array.isArray(rawName)
    ? rawName
    : typeof rawName === "string" && rawName.trim()
      ? [rawName.trim()]
      : [];
  const linkedIns = dataSearchState?.filterComponentData?.contactLinkedIn || [];

  const addNames = () => {
    const raw = nameInput.trim();
    if (!raw) return;
    const performAction = () => {
      const tokens = raw.split(",").map(t => t.trim()).filter(Boolean);
      const current = names;
      const newTokens = tokens.filter(t => !current.includes(t));
      const combined = [...current, ...newTokens].slice(0, MAX_COMPANY_DOMAIN_NAME_ITEMS);
      if (newTokens.length > 0) {
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          name: combined
        });
      }
      setNameInput("");
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction, () => setNameInput(""));
    } else {
      performAction();
    }
  };

  const addLinkedIn = () => {
    const v = linkedinInput.trim();
    if (!v) return;
    if (linkedIns.length >= MAX_COMPANY_DOMAIN_NAME_ITEMS) return;
    const normalized = normalizeLinkedInProfileUrl(v);
    if (!normalized) {
      setLinkedinError(true);
      return;
    }
    if (linkedIns.some(u => normalizeLinkedInProfileUrl(u) === normalized)) {
      setLinkedinInput("");
      return;
    }
    const performAction = () => {
      setLinkedinError(false);
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        contactLinkedIn: [...linkedIns, normalized]
      });
      setLinkedinInput("");
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction, () => setLinkedinInput(""));
    } else {
      performAction();
    }
  };

  return (
    <div className="companyDomainNameSection">
      <div className="filterComponentInputHeader companyDomainNameInputsStack">
        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="contactSearchType"
              checked={contactSearchType === "name"}
              onChange={() => setSearchType("name")}
            />
            <span>Contact name</span>
          </label>
          <InputBox
            name="contactNameInput"
            id="contactNameInput"
            type="text"
            value={nameInput}
            changeAction={val => setNameInput(val)}
            keyupAction={e => Utils.isSubmitKeyAction(e) && addNames()}
            blurAction={() => addNames()}
            label=""
            text=""
            placeholder="e.g. John Smith, Jane Doe"
            inputBoxRef={inputBoxRef}
            focus={true}
            readOnly={contactSearchType !== "name"}
          />
          {contactSearchType === "name" && nameInput.trim() && (
            <p className="filter-input-enter-hint">Press Enter to add</p>
          )}
        </div>
        <div className={`companyDomainNameInputWrap${linkedinError ? " companyDomainNameInputWrap--error" : ""}`}>
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="contactSearchType"
              checked={contactSearchType === "linkedin"}
              onChange={() => setSearchType("linkedin")}
            />
            <span>LinkedIn profile URL</span>
          </label>
          <InputBox
            name="contactLinkedInInput"
            id="contactLinkedInInput"
            type="text"
            value={linkedinInput}
            changeAction={val => {
              setLinkedinInput(val);
              setLinkedinError(false);
            }}
            keyupAction={e => Utils.isSubmitKeyAction(e) && addLinkedIn()}
            blurAction={() => addLinkedIn()}
            label=""
            text=""
            placeholder="e.g. linkedin.com/in/jane-doe"
            readOnly={contactSearchType !== "linkedin"}
          />
          {contactSearchType === "linkedin" && linkedinInput.trim() && (
            <p className="filter-input-enter-hint">Press Enter to add</p>
          )}
        </div>
      </div>
    </div>
  );
});

const ExcludeContactsInputs = observer(() => {
  const fd = dataSearchState?.filterComponentData || {};
  const mode = fd?.excludeContactsMode || "list";
  const selectedListId = fd?.excludeContactListId || null;
  const selectedListName = fd?.excludeContactListName || "";
  const excludedUrls = Array.isArray(fd?.excludeContactLinkedIn) ? fd.excludeContactLinkedIn : [];

  const [listsLoading, setListsLoading] = useState(false);
  const [isSelectingList, setIsSelectingList] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [lists, setLists] = useState([]);
  const [pasteValue, setPasteValue] = useState("");
  const [showListPopup, setShowListPopup] = useState(false);
  const [showUrlsPopup, setShowUrlsPopup] = useState(false);

  const loadLists = async () => {
    setListsLoading(true);
    try {
      const res = await makeApi({
        url: URLS.getContactList,
        method: "POST",
        data: { limit: 1000, pageNo: 1 }
      });
      const list = res?.data?.listResponse?.list || [];
      setLists(Array.isArray(list) ? list.filter(l => l?.name) : []);
    } catch (e) {
      setLists([]);
      toasterState.setToastMsg("Failed to load contact lists.", "fail");
    } finally {
      setListsLoading(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, []);

  useEffect(() => {
    if (mode === "urls") {
      setPasteValue(excludedUrls.join("\n"));
    }
  }, [mode, excludedUrls]);

  const setMode = nextMode => {
    dataSearchState.setFilterComponentData({
      ...fd,
      excludeContactsMode: nextMode
    });
  };

  const listOptions = (lists || []).filter(item =>
    String(item?.name || "").toLowerCase().includes(String(listSearch || "").trim().toLowerCase())
  );

  const fetchListLinkedInUrls = async listId => {
    try {
      // Avoid Solr maxBooleanClauses explosions by fetching contacts in smaller pages.
      const PAGE_SIZE = 500;
      const toLinkedInProfileUrl = contact => {
        const candidates = [
          contact?.linkedinId,
          contact?.linkedIn,
          contact?.linkedin,
          contact?.linkedInProfileUrl,
          contact?.liPublicId
        ];
        for (const rawVal of candidates) {
          const raw = String(rawVal || "").trim();
          if (!raw) continue;
          const normalized = normalizeLinkedInProfileUrl(raw);
          if (normalized) return normalized;
          // Some records store only public profile slug; convert to canonical URL.
          if (/^[a-zA-Z0-9][a-zA-Z0-9-_%]{1,120}$/.test(raw)) {
            return `https://www.linkedin.com/in/${raw}`;
          }
        }
        return "";
      };

      const collected = new Set();
      let pageNo = 1;
      while (collected.size < MAX_EXCLUDE_CONTACT_URLS) {
        const payload = {
          pageNo,
          limit: PAGE_SIZE
        };
        if (listId) {
          payload.listId = String(listId);
        }

        const response = await makeApi({
          url: URLS.getAllContacts,
          method: "POST",
          data: payload
        });

        // makeApi can return non-2xx as { response: { status, data } } instead of throwing.
        if (response?.response) {
          throw new Error(response?.response?.data?.message || "Failed to fetch contacts");
        }

        const data = response?.data || {};
        const contacts = data?.contacts || data?.contactDetails || [];
        if (!Array.isArray(contacts) || contacts.length === 0) break;

        contacts.forEach(c => {
          const li = toLinkedInProfileUrl(c);
          if (li) collected.add(li);
        });

        if (contacts.length < PAGE_SIZE) break;
        pageNo += 1;
      }

      return Array.from(collected).slice(0, MAX_EXCLUDE_CONTACT_URLS);
    } catch (e) {
      return null;
    }
  };

  const selectList = async listItem => {
    const rawListId = listItem?.id || null;
    const isAllList = String(listItem?.name || "").trim().toLowerCase() === "all" || String(rawListId || "").trim().toLowerCase() === "all";
    const listId = isAllList ? null : rawListId;
    dataSearchState.setFilterComponentData({
      ...fd,
      excludeContactsMode: "list",
      excludeContactListId: listId,
      excludeContactListName: listItem?.name || "",
      excludeContactLinkedIn: []
    });
    setIsSelectingList(true);
    dataSearchState.setExcludeContactsLoading(true);
    setShowListPopup(false);
    const urls = await fetchListLinkedInUrls(listId);
    setIsSelectingList(false);
    dataSearchState.setExcludeContactsLoading(false);
    if (urls == null) {
      toasterState.setToastMsg("Failed to load contacts for selected list.", "fail");
      dataSearchState.setFilterComponentData({
        ...fd,
        excludeContactsMode: "list",
        excludeContactListId: null,
        excludeContactListName: "",
        excludeContactLinkedIn: []
      });
      return;
    }
    dataSearchState.setFilterComponentData({
      ...fd,
      excludeContactsMode: "list",
      excludeContactListId: listId,
      excludeContactListName: listItem?.name || "",
      excludeContactLinkedIn: urls
    });
  };

  const applyPastedUrls = () => {
    const parsed = [
      ...new Set(
        String(pasteValue || "")
          .split(/[\n,]+/)
          .map(s => normalizeLinkedInProfileUrl(s))
          .filter(Boolean)
      )
    ];
    if (parsed.length > MAX_EXCLUDE_CONTACT_URLS) {
      toasterState.setToastMsg(`Maximum ${MAX_EXCLUDE_CONTACT_URLS} URLs allowed.`, "fail");
      return;
    }
    dataSearchState.setFilterComponentData({
      ...fd,
      excludeContactsMode: "urls",
      excludeContactListId: null,
      excludeContactListName: "",
      excludeContactLinkedIn: parsed
    });
    setShowUrlsPopup(false);
  };

  return (
    <div className="companyDomainNameSection">
      <div className="filterComponentInputHeader companyDomainNameInputsStack">
        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input type="radio" name="excludeContactsMode" checked={mode === "list"} onChange={() => setMode("list")} disabled={isSelectingList} />
            <span>Select from a list</span>
          </label>
          {mode === "list" && (
            <>
              <div className="companyDomainNamePasteRow">
                <button
                  type="button"
                  className={`companyDomainNamePasteBtn${selectedListId ? " companyDomainNamePasteBtn--hasValue" : ""}`}
                  onClick={() => !isSelectingList && setShowListPopup(true)}
                  disabled={isSelectingList}
                >
                  <i className="material-icons">playlist_add_check</i>
                  {isSelectingList
                    ? "Loading selected list..."
                    : selectedListName
                      ? `Selected: ${selectedListName}`
                      : "Select List"}
                </button>
              </div>
              {selectedListName ? (
                <p className="filter-input-enter-hint">
                  Selected: {selectedListName} ({excludedUrls.length} LinkedIn URLs excluded)
                </p>
              ) : null}
              {isSelectingList ? (
                <p className="filter-input-enter-hint">Loading contacts from selected list...</p>
              ) : null}
            </>
          )}
        </div>

        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input type="radio" name="excludeContactsMode" checked={mode === "urls"} onChange={() => setMode("urls")} disabled={isSelectingList} />
            <span>Paste LinkedIn profile URLs</span>
          </label>
          {mode === "urls" && (
            <>
              <div className="companyDomainNamePasteRow">
                <button
                  type="button"
                  className={`companyDomainNamePasteBtn${excludedUrls.length > 0 ? " companyDomainNamePasteBtn--hasValue" : ""}`}
                  onClick={() => !isSelectingList && setShowUrlsPopup(true)}
                  disabled={isSelectingList}
                >
                  <i className="material-icons">content_paste</i>
                  {excludedUrls.length > 0
                    ? `${excludedUrls.length} URL${excludedUrls.length !== 1 ? "s" : ""} added`
                    : "Add LinkedIn URLs"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {showListPopup && ReactDOM.createPortal(
        <div className="cdnPasteOverlay" onClick={() => setShowListPopup(false)}>
          <div className="cdnPastePopup" onClick={e => e.stopPropagation()}>
            <div className="cdnPastePopup__header">
              <span className="cdnPastePopup__title">Select Contact List</span>
              <button type="button" className="cdnPastePopup__close" onClick={() => setShowListPopup(false)}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <p className="cdnPastePopup__hint">Choose one list. Up to 300 contacts from that list will be excluded by LinkedIn URL.</p>
            <div className="excludeListPopup__search">
              <i className="material-icons excludeListPopup__searchIcon">search</i>
              <input
                className="excludeListPopup__searchInput"
                type="text"
                value={listSearch}
                onChange={e => setListSearch(e.target.value)}
                placeholder="Search list by name"
              />
              {listSearch ? (
                <button
                  type="button"
                  className="excludeListPopup__searchClear"
                  onClick={() => setListSearch("")}
                >
                  <i className="material-icons">close</i>
                </button>
              ) : null}
            </div>
            <div className="excludeListPopup__list">
              {listsLoading ? (
                <div className="excludeListPopup__empty">Loading lists...</div>
              ) : listOptions.length === 0 ? (
                <div className="excludeListPopup__empty">No lists found.</div>
              ) : (
                listOptions.map(item => {
                  const checked =
                    (selectedListId && selectedListId === item?.id) ||
                    (!selectedListId && selectedListName && selectedListName === item?.name);
                  return (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => !isSelectingList && selectList(item)}
                      disabled={isSelectingList}
                      className={`excludeListPopup__row${checked ? " isSelected" : ""}`}
                    >
                      <span className={`excludeListPopup__radio${checked ? " isChecked" : ""}`} />
                      <span className="excludeListPopup__rowName">{item?.name}</span>
                      <span className="excludeListPopup__rowCount">{`${Number(item?.count || 0)} contacts`}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
      {showUrlsPopup && ReactDOM.createPortal(
        <div className="cdnPasteOverlay" onClick={() => setShowUrlsPopup(false)}>
          <div className="cdnPastePopup" onClick={e => e.stopPropagation()}>
            <div className="cdnPastePopup__header">
              <span className="cdnPastePopup__title">Add LinkedIn Profile URLs</span>
              <button type="button" className="cdnPastePopup__close" onClick={() => setShowUrlsPopup(false)}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <p className="cdnPastePopup__hint">
              One LinkedIn profile URL per line (must include /in/). Commas also work. Max {MAX_EXCLUDE_CONTACT_URLS}.
            </p>
            <textarea
              className="cdnPastePopup__textarea"
              placeholder={"https://www.linkedin.com/in/jane-doe\nlinkedin.com/in/john-smith"}
              value={pasteValue}
              onChange={e => setPasteValue(e.target.value)}
            />
            <div className="cdnPastePopup__footer">
              <span className="cdnPastePopup__count">
                {(() => {
                  const valid = [
                    ...new Set(
                      String(pasteValue || "")
                        .split(/[\n,]+/)
                        .map(s => normalizeLinkedInProfileUrl(s))
                        .filter(Boolean)
                    )
                  ].length;
                  return valid ? `${valid} valid URL${valid !== 1 ? "s" : ""}` : "";
                })()}
              </span>
              <div className="cdnPastePopup__actions">
                <button type="button" className="cdnPastePopup__cancel" onClick={() => setShowUrlsPopup(false)}>Cancel</button>
                <button type="button" className="cdnPastePopup__apply" onClick={applyPastedUrls}>Apply</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

const EXPERIENCE_DURATION_TOOLTIP = "Enter minimum and/or maximum duration. Months 0–12. If only min is set, that criteria applies. Maximum must be ≥ minimum when both are set. Values save on Enter or when you leave a field.";

const MONTHS_MAX = 12;
const EXPERIENCE_KEYS = Object.keys(DEFAULT_EXPERIENCE_DURATION);

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const FUNDING_DATE_YEAR_MIN = 2000;
const FUNDING_DATE_YEAR_MAX = new Date().getFullYear();
const FUNDING_DATE_YEARS = Array.from({ length: FUNDING_DATE_YEAR_MAX - FUNDING_DATE_YEAR_MIN + 1 }, (_, i) => FUNDING_DATE_YEAR_MAX - i);

const renderFundingDateHeader = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
  <div className="fundingDatePickerHeader">
    <button type="button" className="fundingDatePickerNav" onClick={decreaseMonth} disabled={prevMonthButtonDisabled} aria-label="Previous month">
      <span className="material-icons">chevron_left</span>
    </button>
    <div className="fundingDatePickerMonthYear">
      <select
        className="fundingDatePickerMonthSelect"
        value={date.getMonth()}
        onChange={e => changeMonth(Number(e.target.value))}
        aria-label="Month"
      >
        {MONTH_NAMES.map((name, i) => (
          <option key={name} value={i}>{name}</option>
        ))}
      </select>
      <select
        className="fundingDatePickerYearSelect"
        value={date.getFullYear()}
        onChange={e => changeYear(Number(e.target.value))}
        aria-label="Year"
      >
        {FUNDING_DATE_YEARS.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
    <button type="button" className="fundingDatePickerNav" onClick={increaseMonth} disabled={nextMonthButtonDisabled} aria-label="Next month">
      <span className="material-icons">chevron_right</span>
    </button>
  </div>
);

const experienceSectionConfig = [
  {
    sectionKey: "total",
    title: "Total Experience",
    minYearKey: "experienceTotalMinYear",
    maxYearKey: "experienceTotalMaxYear",
    minMonthKey: "experienceTotalMinMonth",
    maxMonthKey: "experienceTotalMaxMonth"
  },
  {
    sectionKey: "currentCompany",
    title: "Time in Current Company",
    minYearKey: "experienceCurrentCompanyMinYear",
    maxYearKey: "experienceCurrentCompanyMaxYear",
    minMonthKey: "experienceCurrentCompanyMinMonth",
    maxMonthKey: "experienceCurrentCompanyMaxMonth"
  },
  {
    sectionKey: "currentJob",
    title: "Time in Current Role",
    minYearKey: "experienceCurrentJobMinYear",
    maxYearKey: "experienceCurrentJobMaxYear",
    minMonthKey: "experienceCurrentJobMinMonth",
    maxMonthKey: "experienceCurrentJobMaxMonth"
  }
];

const parseNum = v => {
  if (v === "" || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
const toNum = parseNum;

const ExperienceDurationInputs = ({ experienceValues }) => {
  const [local, setLocal] = useState(() =>
    EXPERIENCE_KEYS.reduce((acc, k) => {
      const v = experienceValues[k] ?? 0;
      acc[k] = v === 0 ? "" : String(v);
      return acc;
    }, {})
  );
  const [errors, setErrors] = useState({});
  const prevValuesRef = useRef(null);

  useEffect(() => {
    const prev = prevValuesRef.current;
    const same = prev && EXPERIENCE_KEYS.every(k => (prev[k] ?? 0) === (experienceValues[k] ?? 0));
    if (same) return;
    prevValuesRef.current = { ...experienceValues };
    setLocal(
      EXPERIENCE_KEYS.reduce((acc, k) => {
        const v = experienceValues[k] ?? 0;
        acc[k] = v === 0 ? "" : String(v);
        return acc;
      }, {})
    );
    setErrors({});
    dataSearchState.setExperienceDurationErrors({});
  }, [experienceValues]);

  useEffect(() => {
    dataSearchState.setExperienceDurationErrors(errors);
  }, [errors]);

  const handleChange = (key, value) => {
    setLocal(prev => ({ ...prev, [key]: value }));
  };

  const validate = (minY, minM, maxY, maxM) => {
    if (minM > MONTHS_MAX || maxM > MONTHS_MAX) return "Months must be between 0 and 12.";
    const minTotal = minY * 12 + minM;
    const maxTotal = maxY * 12 + maxM;
    if (maxTotal > 0 && maxTotal < minTotal) return "Maximum must be greater than or equal to minimum.";
    return null;
  };

  const commitSection = (sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey) => {
    const minY = parseNum(local[minYearKey]);
    const minM = parseNum(local[minMonthKey]);
    const maxY = parseNum(local[maxYearKey]);
    const maxM = parseNum(local[maxMonthKey]);
    const err = validate(minY, minM, maxY, maxM);
    setErrors(prev => {
      const next = { ...prev, [sectionKey]: err };
      dataSearchState.setExperienceDurationErrors(next);
      return next;
    });
    if (err) return;
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      [minYearKey]: minY,
      [minMonthKey]: minM,
      [maxYearKey]: maxY,
      [maxMonthKey]: maxM
    });
  };

  const onBlur = (sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey) => {
    requestAnimationFrame(() => commitSection(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey));
  };

  const onKeyDown = (e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitSection(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey);
    }
  };

  return (
    <div className="experienceDurationSection">
      {experienceSectionConfig.map(({ sectionKey, title, minYearKey, maxYearKey, minMonthKey, maxMonthKey }) => (
        <div key={sectionKey} className="experienceDurationBlock">
          <div className="experienceDurationBlockHeader">
            <span className="experienceDurationBlockTitle">{title}</span>
            <Tooltip tooltip={EXPERIENCE_DURATION_TOOLTIP} placement="top">
              <span className="experienceDurationBlockInfoIcon material-icons" aria-label="Duration rules">info</span>
            </Tooltip>
          </div>
          <div className="experienceDurationGrid">
            <div className="experienceDurationColLabel experienceDurationColYears">Years</div>
            <div className="experienceDurationColDash" aria-hidden="true" />
            <div className="experienceDurationColLabel experienceDurationColMonths">Months</div>

            <div className="experienceDurationCell experienceDurationYearCell">
              <input
                type="number"
                min={0}
                placeholder="Min Years"
                className="experienceDurationInput"
                value={local[minYearKey] ?? ""}
                onChange={e => handleChange(minYearKey, e.target.value)}
                onBlur={() => onBlur(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                onKeyDown={e => onKeyDown(e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                aria-label={`${title} min years`}
              />
            </div>
            <div className="experienceDurationCell experienceDurationDashCell">
              <span className="experienceDurationDash">-</span>
            </div>
            <div className="experienceDurationCell experienceDurationMonthCell">
              <input
                type="number"
                min={0}
                max={MONTHS_MAX}
                placeholder="Min Months"
                className="experienceDurationInput experienceDurationInputMonths"
                value={local[minMonthKey] ?? ""}
                onChange={e => handleChange(minMonthKey, e.target.value)}
                onBlur={() => onBlur(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                onKeyDown={e => onKeyDown(e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                aria-label={`${title} min months`}
              />
            </div>

            <div className="experienceDurationCell experienceDurationYearCell">
              <input
                type="number"
                min={0}
                placeholder="Max Years"
                className="experienceDurationInput"
                value={local[maxYearKey] ?? ""}
                onChange={e => handleChange(maxYearKey, e.target.value)}
                onBlur={() => onBlur(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                onKeyDown={e => onKeyDown(e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                aria-label={`${title} max years`}
              />
            </div>
            <div className="experienceDurationCell experienceDurationDashCell">
              <span className="experienceDurationDash">-</span>
            </div>
            <div className="experienceDurationCell experienceDurationMonthCell">
              <input
                type="number"
                min={0}
                max={MONTHS_MAX}
                placeholder="Max Months"
                className="experienceDurationInput experienceDurationInputMonths"
                value={local[maxMonthKey] ?? ""}
                onChange={e => handleChange(maxMonthKey, e.target.value)}
                onBlur={() => onBlur(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                onKeyDown={e => onKeyDown(e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                aria-label={`${title} max months`}
              />
            </div>
          </div>
          {errors[sectionKey] && (
            <div className="experienceDurationError" role="alert">
              {errors[sectionKey]}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const experienceValuesEqual = (a, b) =>
  Object.keys(DEFAULT_EXPERIENCE_DURATION).every(k => (a[k] ?? 0) === (b[k] ?? 0));

const ExperienceDurationInputsWrapper = observer(() => {
  const fd = dataSearchState?.filterComponentData || {};
  const experienceValues = useMemo(
    () =>
      Object.keys(DEFAULT_EXPERIENCE_DURATION).reduce((acc, k) => {
        acc[k] = fd[k] ?? 0;
        return acc;
      }, {}),
    [
      fd.experienceCurrentJobMinYear,
      fd.experienceCurrentJobMaxYear,
      fd.experienceCurrentJobMinMonth,
      fd.experienceCurrentJobMaxMonth,
      fd.experienceCurrentCompanyMinYear,
      fd.experienceCurrentCompanyMaxYear,
      fd.experienceCurrentCompanyMinMonth,
      fd.experienceCurrentCompanyMaxMonth,
      fd.experienceTotalMinYear,
      fd.experienceTotalMaxYear,
      fd.experienceTotalMinMonth,
      fd.experienceTotalMaxMonth
    ]
  );
  return <ExperienceDurationInputsMemo experienceValues={experienceValues} />;
});

const ExperienceDurationInputsMemo = memo(ExperienceDurationInputs, (prev, next) =>
  experienceValuesEqual(prev.experienceValues, next.experienceValues)
);

/**
 * Self-contained inline job function accordion with live search — no position:absolute.
 * Replaces JobFunctionDropdown/CustomMultiSelect so the list stays in document flow.
 */
const JobFunctionAccordion = observer(() => {
  const [query, setQuery] = useState("");
  const [openCats, setOpenCats] = useState([]);
  const selected = dataSearchState?.filterComponentData?.department || [];
  const selectedSet = new Set(selected);

  const lower = query.trim().toLowerCase();

  const visibleCategories = JOB_FUNCTION_CATEGORIES.map(cat => {
    if (!lower) return { ...cat, visibleItems: cat.items };
    const catMatch = cat.label.toLowerCase().includes(lower);
    const visibleItems = catMatch ? cat.items : cat.items.filter(i => i.label.toLowerCase().includes(lower));
    return { ...cat, visibleItems };
  }).filter(cat => cat.visibleItems.length > 0);

  // Auto-expand matching categories when searching
  useEffect(() => {
    if (lower) {
      const matchingIds = JOB_FUNCTION_CATEGORIES.map(cat => {
        const catMatch = cat.label.toLowerCase().includes(lower);
        const hasItemMatch = cat.items.some(i => i.label.toLowerCase().includes(lower));
        return (catMatch || hasItemMatch) ? cat.id : null;
      }).filter(Boolean);
      setOpenCats(matchingIds);
    } else {
      setOpenCats([]);
    }
  }, [lower]);

  const toggleCat = catId => {
    setOpenCats(prev =>
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  const toggleItem = itemId => {
    const next = selectedSet.has(itemId)
      ? selected.filter(id => id !== itemId)
      : [...selected, itemId];
    dataSearchState.setFilterComponentData({ ...dataSearchState.filterComponentData, department: next });
  };

  const toggleCatAll = cat => {
    const allIds = cat.visibleItems.map(i => i.id);
    const allChecked = allIds.every(id => selectedSet.has(id));
    const next = allChecked
      ? selected.filter(id => !allIds.includes(id))
      : [...new Set([...selected, ...allIds])];
    dataSearchState.setFilterComponentData({ ...dataSearchState.filterComponentData, department: next });
  };

  return (
    <div className="ftAcc">
      {/* Search input */}
      <div className="ftAcc__search">
        <input
          type="text"
          className="ftAcc__search-input"
          placeholder="Search Job Function"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {query ? (
          <i className="material-icons ftAcc__search-icon ftAcc__search-clear" onClick={() => setQuery("")}>close</i>
        ) : (
          <i className="material-icons ftAcc__search-icon">search</i>
        )}
      </div>

      {/* Scrollable category list */}
      <div className="ftAcc__list ftAcc__list--tall">
        {visibleCategories.length === 0 && (
          <div className="ftAcc__empty">No results found</div>
        )}
        {visibleCategories.map(cat => {
          const allIds = cat.visibleItems.map(i => i.id);
          const checkedCount = allIds.filter(id => selectedSet.has(id)).length;
          const allChecked = checkedCount === allIds.length && allIds.length > 0;
          const indeterminate = checkedCount > 0 && !allChecked;
          const isOpen = openCats.includes(cat.id);
          return (
            <div key={cat.id} className="ftAcc__cat">
              <div className="ftAcc__cat-row">
                <button type="button" className="ftAcc__cat-expand" onClick={() => toggleCat(cat.id)}>
                  <span className="material-icons">{isOpen ? "keyboard_arrow_down" : "keyboard_arrow_right"}</span>
                </button>
                <label className="ftAcc__cat-label" onClick={() => toggleCatAll(cat)}>
                  <span className={`ftAcc__checkbox${allChecked ? " ftAcc__checkbox--checked" : indeterminate ? " ftAcc__checkbox--indeterminate" : ""}`} />
                  <span className="ftAcc__cat-name">{cat.label}</span>
                </label>
              </div>
              {isOpen && (
                <div className="ftAcc__items">
                  {cat.visibleItems.map(item => (
                    <label key={item.id} className="ftAcc__item" onClick={() => toggleItem(item.id)}>
                      <span className={`ftAcc__checkbox${selectedSet.has(item.id) ? " ftAcc__checkbox--checked" : ""}`} />
                      <span className="ftAcc__item-name">{item.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

/**
 * Self-contained inline funding type accordion — no position:absolute anywhere.
 * Replaces CustomMultiSelect for the funding panel so the list stays in document flow.
 */
const FundingTypeAccordion = observer(() => {
  const [listOpen, setListOpen] = useState(false);
  const [openCats, setOpenCats] = useState([]);
  const selected = dataSearchState?.filterComponentData?.funding || [];
  const selectedSet = new Set(selected);

  const toggleCat = catId => {
    setOpenCats(prev =>
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  const toggleItem = itemId => {
    const next = selectedSet.has(itemId)
      ? selected.filter(id => id !== itemId)
      : [...selected, itemId];
    dataSearchState.setFilterComponentData({ ...dataSearchState.filterComponentData, funding: next });
  };

  const toggleCatAll = cat => {
    const allIds = cat.items.map(i => i.id);
    const allChecked = allIds.every(id => selectedSet.has(id));
    const next = allChecked
      ? selected.filter(id => !allIds.includes(id))
      : [...new Set([...selected, ...allIds])];
    dataSearchState.setFilterComponentData({ ...dataSearchState.filterComponentData, funding: next });
  };

  return (
    <div className="ftAcc">
      {/* Trigger button */}
      <button
        type="button"
        className={`ftAcc__trigger${listOpen ? " ftAcc__trigger--open" : ""}${selected.length > 0 ? " hasValue" : ""}`}
        onClick={() => setListOpen(v => !v)}
      >
        <span className="ftAcc__trigger-label">
          Funding type
          {selected.length > 0 && (
            <span className="ftAcc__trigger-count">{selected.length} selected</span>
          )}
        </span>
        <span className="material-icons ftAcc__trigger-icon">
          {listOpen ? "expand_less" : "expand_more"}
        </span>
      </button>

      {/* Scrollable category list */}
      {listOpen && (
        <div className="ftAcc__list">
          {FUNDING_CATEGORIES.map(cat => {
            const allIds = cat.items.map(i => i.id);
            const checkedCount = allIds.filter(id => selectedSet.has(id)).length;
            const allChecked = checkedCount === allIds.length;
            const indeterminate = checkedCount > 0 && !allChecked;
            const isOpen = openCats.includes(cat.id);
            return (
              <div key={cat.id} className="ftAcc__cat">
                <div className="ftAcc__cat-row">
                  <button
                    type="button"
                    className="ftAcc__cat-expand"
                    onClick={() => toggleCat(cat.id)}
                    aria-label={isOpen ? "Collapse" : "Expand"}
                  >
                    <span className="material-icons">{isOpen ? "keyboard_arrow_down" : "keyboard_arrow_right"}</span>
                  </button>
                  <label className="ftAcc__cat-label">
                    <span
                      className={`ftAcc__checkbox${allChecked ? " ftAcc__checkbox--checked" : indeterminate ? " ftAcc__checkbox--indeterminate" : ""}`}
                      onClick={() => toggleCatAll(cat)}
                    />
                    <span className="ftAcc__cat-name">{cat.label}</span>
                  </label>
                </div>
                {isOpen && (
                  <div className="ftAcc__items">
                    {cat.items.map(item => (
                      <label key={item.id} className="ftAcc__item">
                        <span
                          className={`ftAcc__checkbox${selectedSet.has(item.id) ? " ftAcc__checkbox--checked" : ""}`}
                          onClick={() => toggleItem(item.id)}
                        />
                        <span className="ftAcc__item-name">{item.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// Funding range inputs: Last Funding Round Date (calendar), Last/Total Amount (min/max $)
const FundingRangeInputs = observer(() => {
  const d = dataSearchState?.filterComponentData || {};
  const durationStart = d.fundingDurationStart != null && d.fundingDurationStart !== ""
    ? new Date(Number(d.fundingDurationStart))
    : null;
  const durationEnd = d.fundingDurationEnd != null && d.fundingDurationEnd !== ""
    ? new Date(Number(d.fundingDurationEnd))
    : null;

  const setDuration = (key, date) => {
    const ts = date ? String(new Date(date).setHours(0, 0, 0, 0)) : null;
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      [key]: ts
    });
  };

  const setAmount = (key, value) => {
    const num = value === "" || value == null ? null : (Number(value) >= 0 ? Number(value) : null);
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      [key]: num
    });
  };

  return (
    <div className="fundingRangeSection">
      <div className="fundingRangeBlock">
        <div className="fundingRangeBlockTitle">Last Funding Round Date</div>
        <div className="fundingRangeRow fundingRangeDateRow">
          <div className="fundingRangeDateWrap fundingDatePickerWrap">
            <ReactDatePicker
              id="fundingDurationFrom"
              selected={durationStart}
              onChange={date => setDuration("fundingDurationStart", date)}
              placeholderText="From"
              dateFormat="MMM d, yyyy"
              isClearable
              className="fundingRangeInput fundingRangeDateInput"
              minDate={new Date(FUNDING_DATE_YEAR_MIN, 0, 1)}
              maxDate={new Date()}
              renderCustomHeader={renderFundingDateHeader}
              popperClassName="fundingDatePickerPopper"
              popperPlacement="bottom-start"
              popperProps={{ strategy: "fixed" }}
              portalId="funding-datepicker-portal"
            />
            <span
              className="fundingRangeDateIcon material-icons"
              role="button"
              tabIndex={-1}
              aria-label="Toggle calendar"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                const wrap = e.currentTarget.closest(".fundingRangeDateWrap");
                const input = wrap?.querySelector(".react-datepicker-wrapper input");
                if (!input) return;
                const isInputFocused = document.activeElement === input;
                const popper = document.querySelector(".react-datepicker-popper.fundingDatePickerPopper");
                const isPopperVisible = popper && popper.getBoundingClientRect().height > 0;
                if (isInputFocused || isPopperVisible) {
                  input.blur();
                } else {
                  input.focus();
                }
              }}
            >
              calendar_today
            </span>
          </div>
          <div className="fundingRangeDateWrap fundingDatePickerWrap">
            <ReactDatePicker
              id="fundingDurationTo"
              selected={durationEnd}
              onChange={date => setDuration("fundingDurationEnd", date)}
              placeholderText="To"
              dateFormat="MMM d, yyyy"
              isClearable
              className="fundingRangeInput fundingRangeDateInput"
              minDate={durationStart ? new Date(Math.max(durationStart.getTime(), new Date(FUNDING_DATE_YEAR_MIN, 0, 1).getTime())) : new Date(FUNDING_DATE_YEAR_MIN, 0, 1)}
              maxDate={new Date()}
              renderCustomHeader={renderFundingDateHeader}
              popperClassName="fundingDatePickerPopper"
              popperPlacement="bottom-start"
              popperProps={{ strategy: "fixed" }}
              portalId="funding-datepicker-portal"
            />
            <span
              className="fundingRangeDateIcon material-icons"
              role="button"
              tabIndex={-1}
              aria-label="Toggle calendar"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                const wrap = e.currentTarget.closest(".fundingRangeDateWrap");
                const input = wrap?.querySelector(".react-datepicker-wrapper input");
                if (!input) return;
                const isInputFocused = document.activeElement === input;
                const popper = document.querySelector(".react-datepicker-popper.fundingDatePickerPopper");
                const isPopperVisible = popper && popper.getBoundingClientRect().height > 0;
                if (isInputFocused || isPopperVisible) {
                  input.blur();
                } else {
                  input.focus();
                }
              }}
            >
              calendar_today
            </span>
          </div>
        </div>
      </div>
      <div className="fundingRangeBlock">
        <div className="fundingRangeBlockTitle">Last Funding Round Amount</div>
        <div className="fundingRangeRow">
          <div className="fundingRangeAmountWrap">
            <span className="fundingRangeDollar">$</span>
            <input
              type="number"
              min={0}
              placeholder="Min"
              className="fundingRangeInput fundingRangeAmountInput"
              value={d.fundingLastAmountStart ?? ""}
              onChange={e => setAmount("fundingLastAmountStart", e.target.value === "" ? "" : e.target.value)}
              aria-label="Last funding round amount min"
            />
          </div>
          <span className="fundingRangeDash">-</span>
          <div className="fundingRangeAmountWrap">
            <span className="fundingRangeDollar">$</span>
            <input
              type="number"
              min={0}
              placeholder="Max"
              className="fundingRangeInput fundingRangeAmountInput"
              value={d.fundingLastAmountEnd ?? ""}
              onChange={e => setAmount("fundingLastAmountEnd", e.target.value === "" ? "" : e.target.value)}
              aria-label="Last funding round amount max"
            />
          </div>
        </div>
      </div>
      <div className="fundingRangeBlock">
        <div className="fundingRangeBlockTitle">Total Funding Amount</div>
        <div className="fundingRangeRow">
          <div className="fundingRangeAmountWrap">
            <span className="fundingRangeDollar">$</span>
            <input
              type="number"
              min={0}
              placeholder="Min"
              className="fundingRangeInput fundingRangeAmountInput"
              value={d.fundingTotalAmountStart ?? ""}
              onChange={e => setAmount("fundingTotalAmountStart", e.target.value === "" ? "" : e.target.value)}
              aria-label="Total funding amount min"
            />
          </div>
          <span className="fundingRangeDash">-</span>
          <div className="fundingRangeAmountWrap">
            <span className="fundingRangeDollar">$</span>
            <input
              type="number"
              min={0}
              placeholder="Max"
              className="fundingRangeInput fundingRangeAmountInput"
              value={d.fundingTotalAmountEnd ?? ""}
              onChange={e => setAmount("fundingTotalAmountEnd", e.target.value === "" ? "" : e.target.value)}
              aria-label="Total funding amount max"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

/** Title filter panel: include/exclude toggle + chips with flip ability. */
const TitleFilterPanel = observer(({ item, setTableLoading }) => {
  const { name: placeholder = "Title" } = item || {};
  const [mode, setMode] = useState("include");
  const [inputValue, setInputValue] = useState("");
  const inputRef = createRef();

  const handleAdd = () => {
    const raw = (inputValue || "").trim();
    if (!raw) { inputRef.current && inputRef.current.focus(); return; }
    const tokens = raw.split(",").map(t => t.trim()).filter(Boolean);
    const performAction = () => {
      const fd = dataSearchState.filterComponentData;
      const key = mode === "include" ? "title" : "titleExclude";
      const current = fd[key] || [];
      const newTokens = tokens.filter(t => !current.includes(t));
      const combined = [...current, ...newTokens];
      dataSearchState.setFilterComponentData({ ...fd, [key]: combined });
      setInputValue("");
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction, () => setInputValue(""));
    } else { performAction(); }
  };

  const handleRemove = (val, key) => {
    const performAction = () => {
      const fd = dataSearchState.filterComponentData;
      dataSearchState.setFilterComponentData({ ...fd, [key]: (fd[key] || []).filter(v => v !== val) });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const handleFlip = (val, fromKey) => {
    const performAction = () => {
      const toKey = fromKey === "title" ? "titleExclude" : "title";
      const fd = dataSearchState.filterComponentData;
      const from = (fd[fromKey] || []).filter(v => v !== val);
      const to = [...new Set([...(fd[toKey] || []), val])];
      dataSearchState.setFilterComponentData({ ...fd, [fromKey]: from, [toKey]: to });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const handleClearAll = () => {
    const performAction = () => {
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        title: [],
        titleExclude: []
      });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const includeTitles = dataSearchState.filterComponentData.title || [];
  const excludeTitles = dataSearchState.filterComponentData.titleExclude || [];
  const totalCount = includeTitles.length + excludeTitles.length;

  return (
    <>
      {/* Primary active role only */}
      <div className="primaryActiveRoleOnlyRow">
        <label className="primaryActiveRoleOnlyLabel">
          <input
            type="checkbox"
            checked={!!dataSearchState.filterComponentData.primaryActiveRoleOnly}
            onChange={e => {
              dataSearchState.setFilterComponentData({
                ...dataSearchState.filterComponentData,
                primaryActiveRoleOnly: e.target.checked
              });
            }}
            className="primaryActiveRoleOnlyCheckbox"
          />
          <span>Primary active role only</span>
          <Tooltip
            tooltip="Only matches if the job title is the most recent active entry on the profile. This filters out secondary concurrent roles (e.g. Advisor, Board Member) that are not the person's primary focus."
            placement="top"
            tooltipClassName="tooltip-narrow"
          >
            <span className="primaryActiveRoleOnlyInfoIcon material-icons" aria-label="Primary active role info">info</span>
          </Tooltip>
        </label>
      </div>

      {/* Strict match */}
      <div className="primaryActiveRoleOnlyRow">
        <label className="primaryActiveRoleOnlyLabel">
          <input
            type="checkbox"
            checked={!!dataSearchState.filterComponentData.titleExactMatch}
            onChange={e => {
              dataSearchState.setFilterComponentData({
                ...dataSearchState.filterComponentData,
                titleExactMatch: e.target.checked
              });
            }}
            className="primaryActiveRoleOnlyCheckbox"
          />
          <span>Strict match</span>
          <Tooltip
            tooltip="Only returns contacts whose title exactly matches the entered terms. Disable for broader fuzzy matching."
            placement="top"
            tooltipClassName="tooltip-narrow"
          >
            <span className="primaryActiveRoleOnlyInfoIcon material-icons" aria-label="Strict match info">info</span>
          </Tooltip>
        </label>
      </div>

      {/* Include / Exclude mode toggle */}
      <div className="titleModeToggle">
        <button
          type="button"
          className={`titleModeBtn${mode === "include" ? " titleModeBtn--active titleModeBtn--include" : ""}`}
          onClick={() => setMode("include")}
        >
          Include
        </button>
        <button
          type="button"
          className={`titleModeBtn${mode === "exclude" ? " titleModeBtn--active titleModeBtn--exclude" : ""}`}
          onClick={() => setMode("exclude")}
        >
          Exclude
        </button>
      </div>

      {/* Text input */}
      <div className="filterComponentInputHeader">
        <InputBox
          name="title"
          id="title"
          type="text"
          value={inputValue}
          changeAction={setInputValue}
          keyupAction={e => { if (Utils.isSubmitKeyAction(e)) handleAdd(); }}
          blurAction={() => handleAdd()}
          label=""
          text=""
          placeholder="e.g. CEO, CTO, CFO"
          inputBoxRef={inputRef}
          focus={true}
        />
        {totalCount > 0 && (
          <div className="flexCenter">
            <div className="filterCount">{totalCount}</div>
          </div>
        )}
      </div>
      <p className="filter-input-enter-hint">Press Enter to add</p>

      {/* Include + Exclude chips */}
      {totalCount > 0 && (
        <section className="addRemoveValue">
          <span className="horizontalBorderLine" />
          <div className="flexCenterWithJustifyContent">
            <div className="addRemoveValueWrapper titleChipsWrapper">
              {includeTitles.map((val, idx) => (
                <div className="tagItem tagItem--include" key={`inc-${val}-${idx}`}>
                  <span className="selectedTag">{val}</span>
                  <Tooltip tooltip="Switch to Exclude" placement="top">
                    <i
                      className="material-icons titleChipFlipIcon"
                      onClick={() => handleFlip(val, "title")}
                      role="button"
                      aria-label="Switch to Exclude"
                    >swap_horiz</i>
                  </Tooltip>
                  <i
                    className="material-icons selectedTagCloseIcon"
                    onClick={() => handleRemove(val, "title")}
                    role="button"
                    aria-label={`Remove ${val}`}
                  >close</i>
                </div>
              ))}
              {excludeTitles.map((val, idx) => (
                <div className="tagItem tagItem--exclude" key={`exc-${val}-${idx}`}>
                  <span className="selectedTag">{val}</span>
                  <Tooltip tooltip="Switch to Include" placement="top">
                    <i
                      className="material-icons titleChipFlipIcon"
                      onClick={() => handleFlip(val, "titleExclude")}
                      role="button"
                      aria-label="Switch to Include"
                    >swap_horiz</i>
                  </Tooltip>
                  <i
                    className="material-icons selectedTagCloseIcon"
                    onClick={() => handleRemove(val, "titleExclude")}
                    role="button"
                    aria-label={`Remove ${val}`}
                  >close</i>
                </div>
              ))}
            </div>
            <div className="clearAll">
              <Tooltip tooltip="Clear all" placement="left">
                <i className="material-icons clearAllIcon" onClick={handleClearAll}>clear_all</i>
              </Tooltip>
            </div>
          </div>
        </section>
      )}
    </>
  );
});

/** Industry filter panel: include/exclude toggle + dropdown + chips with flip ability. */
const IndustryFilterPanel = observer(({ item, activeFilterIndex, activeIndex, setActiveIndex, setTableLoading }) => {
  const [mode, setMode] = useState("include");

  const includeItems = dataSearchState?.selectedFilteredList?.industry || [];
  const excludeItems = dataSearchState?.selectedFilteredList?.industryExclude || [];
  const totalCount = includeItems.length + excludeItems.length;

  const handleFlip = (entry, fromKey) => {
    const toKey = fromKey === "industry" ? "industryExclude" : "industry";
    const fd = dataSearchState.filterComponentData;
    const sl = dataSearchState.selectedFilteredList;
    const entryId = typeof entry === "object" ? (entry?.id ?? entry?.name) : entry;
    const entryName = typeof entry === "object" ? (entry?.name ?? entry?.id) : entry;
    const performAction = () => {
      dataSearchState.setSelectedFilteredList({
        ...sl,
        [fromKey]: (sl[fromKey] || []).filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== entryId),
        [toKey]: [...new Set([...(sl[toKey] || []).filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== entryId), entry])]
      });
      dataSearchState.setFilterComponentData({
        ...fd,
        [fromKey]: (fd[fromKey] || []).filter(v => v !== entryName),
        [toKey]: [...new Set([...(fd[toKey] || []), entryName])]
      });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const handleRemove = (entry, key) => {
    const fd = dataSearchState.filterComponentData;
    const sl = dataSearchState.selectedFilteredList;
    const entryId = typeof entry === "object" ? (entry?.id ?? entry?.name) : entry;
    const entryName = typeof entry === "object" ? (entry?.name ?? entry?.id) : entry;
    const performAction = () => {
      dataSearchState.setSelectedFilteredList({
        ...sl,
        [key]: (sl[key] || []).filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== entryId)
      });
      dataSearchState.setFilterComponentData({
        ...fd,
        [key]: (fd[key] || []).filter(v => v !== entryName)
      });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const handleClearAll = () => {
    const performAction = () => {
      dataSearchState.setSelectedFilteredList({
        ...dataSearchState.selectedFilteredList,
        industry: [],
        industryExclude: []
      });
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        industry: [],
        sector: [],
        industryExclude: []
      });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const dropdownId = mode === "include" ? "industry" : "industryExclude";

  return (
    <>
      {/* Include / Exclude mode toggle */}
      <div className="titleModeToggle">
        <button
          type="button"
          className={`titleModeBtn${mode === "include" ? " titleModeBtn--active titleModeBtn--include" : ""}`}
          onClick={() => setMode("include")}
        >
          Include
        </button>
        <button
          type="button"
          className={`titleModeBtn${mode === "exclude" ? " titleModeBtn--active titleModeBtn--exclude" : ""}`}
          onClick={() => setMode("exclude")}
        >
          Exclude
        </button>
      </div>

      {/* The industry dropdown - keyed by mode so it remounts on toggle */}
      <AutoCompleteMultiSelectDropdown
        key={dropdownId}
        {...item}
        id={dropdownId}
        discoverPage={true}
        showSearchPanel={true}
        showDropdown={true}
        setActiveIndex={setActiveIndex}
        disableOverlayClick={true}
      />

      {/* Intentionally hide selected chips inside Industry overlay to preserve space.
          Selected values are reflected in filter count/chips outside the overlay. */}
    </>
  );
});

/** Helper to create a keyword chip object from a string. */
const toKeywordChip = val => ({
  id: val,
  name: val,
  checked: true,
  group: "keywords",
  searchText: [val]
});

/** Keywords filter panel (company or contact): include/exclude toggle + input text box + chips with flip ability (like title). */
const KeywordsFilterPanel = observer(({ item, setTableLoading }) => {
  const id = item?.id || "companyKeywords";
  const placeholder = id === "companyKeywords" ? "e.g. SaaS, B2B, enterprise" : "e.g. sales, growth, CRM";
  const [mode, setMode] = useState("include");
  const [inputValue, setInputValue] = useState("");
  const inputRef = createRef();
  const excludeId = id === "companyKeywords" ? "companyKeywordsExclude" : "contactKeywordsExclude";

  const includeItems = dataSearchState?.selectedFilteredList?.[id] || [];
  const excludeItems = dataSearchState?.selectedFilteredList?.[excludeId] || [];
  const totalCount = includeItems.length + excludeItems.length;

  const handleAdd = () => {
    const raw = (inputValue || "").trim();
    if (!raw) { inputRef.current && inputRef.current.focus(); return; }
    const tokens = raw.split(",").map(t => t.trim()).filter(Boolean);
    const key = mode === "include" ? id : excludeId;
    const fd = dataSearchState.filterComponentData;
    const sl = dataSearchState.selectedFilteredList;
    const current = fd[key] || [];
    const newTokens = tokens.filter(t => !current.includes(t));
    if (!newTokens.length) { setInputValue(""); return; }
    const performAction = () => {
      dataSearchState.setSelectedFilteredList({
        ...sl,
        [key]: [...(sl[key] || []), ...newTokens.map(t => toKeywordChip(t))]
      });
      dataSearchState.setFilterComponentData({
        ...fd,
        [key]: [...current, ...newTokens]
      });
      setInputValue("");
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction, () => setInputValue(""));
    } else { performAction(); }
  };

  const handleFlip = (entry, fromKey) => {
    const toKey = fromKey === id ? excludeId : id;
    const fd = dataSearchState.filterComponentData;
    const sl = dataSearchState.selectedFilteredList;
    const entryId = typeof entry === "object" ? (entry?.id ?? entry?.name) : entry;
    const entryVal = typeof entry === "object" ? (entry?.name ?? entry?.id ?? entry) : entry;
    const performAction = () => {
      dataSearchState.setSelectedFilteredList({
        ...sl,
        [fromKey]: (sl[fromKey] || []).filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== entryId),
        [toKey]: [...new Set([...(sl[toKey] || []).filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== entryId), entry])]
      });
      const fromArr = (fd[fromKey] || []).filter(v => v !== entryVal);
      const toArr = [...new Set([...(fd[toKey] || []), entryVal])];
      dataSearchState.setFilterComponentData({ ...fd, [fromKey]: fromArr, [toKey]: toArr });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const handleRemove = (entry, key) => {
    const fd = dataSearchState.filterComponentData;
    const sl = dataSearchState.selectedFilteredList;
    const entryId = typeof entry === "object" ? (entry?.id ?? entry?.name) : entry;
    const entryVal = typeof entry === "object" ? (entry?.name ?? entry?.id ?? entry) : entry;
    const performAction = () => {
      dataSearchState.setSelectedFilteredList({
        ...sl,
        [key]: (sl[key] || []).filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== entryId)
      });
      dataSearchState.setFilterComponentData({
        ...fd,
        [key]: (fd[key] || []).filter(v => v !== entryVal)
      });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const handleClearAll = () => {
    const performAction = () => {
      dataSearchState.setSelectedFilteredList({
        ...dataSearchState.selectedFilteredList,
        [id]: [],
        [excludeId]: []
      });
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        [id]: [],
        [excludeId]: []
      });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  return (
    <>
      <div className="titleModeToggle">
        <button
          type="button"
          className={`titleModeBtn${mode === "include" ? " titleModeBtn--active titleModeBtn--include" : ""}`}
          onClick={() => setMode("include")}
        >
          Include
        </button>
        <button
          type="button"
          className={`titleModeBtn${mode === "exclude" ? " titleModeBtn--active titleModeBtn--exclude" : ""}`}
          onClick={() => setMode("exclude")}
        >
          Exclude
        </button>
      </div>

      <div className="filterComponentInputHeader">
        <InputBox
          name={id}
          id={id}
          type="text"
          value={inputValue}
          changeAction={setInputValue}
          keyupAction={e => { if (Utils.isSubmitKeyAction(e)) handleAdd(); }}
          blurAction={() => handleAdd()}
          label=""
          text=""
          placeholder={placeholder}
          inputBoxRef={inputRef}
          focus={true}
        />
        {totalCount > 0 && (
          <div className="flexCenter">
            <div className="filterCount">{totalCount}</div>
          </div>
        )}
      </div>
      <p className="filter-input-enter-hint">Press Enter to add</p>

      {totalCount > 0 && (
        <section className="addRemoveValue">
          <span className="horizontalBorderLine" />
          <div className="flexCenterWithJustifyContent">
            <div className="addRemoveValueWrapper titleChipsWrapper">
              {includeItems.map((entry, idx) => {
                const label = typeof entry === "object" ? (entry?.name ?? entry?.id) : entry;
                const eid = typeof entry === "object" ? (entry?.id ?? entry?.name) : entry;
                return (
                  <div className="tagItem tagItem--include" key={`inc-${eid}-${idx}`}>
                    <span className="selectedTag">{label}</span>
                    <Tooltip tooltip="Switch to Exclude" placement="top">
                      <i className="material-icons titleChipFlipIcon" onClick={() => handleFlip(entry, id)} role="button" aria-label="Switch to Exclude">swap_horiz</i>
                    </Tooltip>
                    <i className="material-icons selectedTagCloseIcon" onClick={() => handleRemove(entry, id)} role="button" aria-label={`Remove ${label}`}>close</i>
                  </div>
                );
              })}
              {excludeItems.map((entry, idx) => {
                const label = typeof entry === "object" ? (entry?.name ?? entry?.id) : entry;
                const eid = typeof entry === "object" ? (entry?.id ?? entry?.name) : entry;
                return (
                  <div className="tagItem tagItem--exclude" key={`exc-${eid}-${idx}`}>
                    <span className="selectedTag">{label}</span>
                    <Tooltip tooltip="Switch to Include" placement="top">
                      <i className="material-icons titleChipFlipIcon" onClick={() => handleFlip(entry, excludeId)} role="button" aria-label="Switch to Include">swap_horiz</i>
                    </Tooltip>
                    <i className="material-icons selectedTagCloseIcon" onClick={() => handleRemove(entry, excludeId)} role="button" aria-label={`Remove ${label}`}>close</i>
                  </div>
                );
              })}
            </div>
            <div className="clearAll">
              <Tooltip tooltip="Clear all" placement="left">
                <i className="material-icons clearAllIcon" onClick={handleClearAll}>clear_all</i>
              </Tooltip>
            </div>
          </div>
        </section>
      )}
    </>
  );
});

/** Generic text-array filter panel: include/exclude toggle + input + chips with flip (for skill, certification, etc.). */
const TextArrayFilterPanel = observer(({ item, setTableLoading, includeKey, excludeKey, placeholder }) => {
  const [mode, setMode] = useState("include");
  const [inputValue, setInputValue] = useState("");
  const inputRef = createRef();

  const handleAdd = () => {
    const raw = (inputValue || "").trim();
    if (!raw) { inputRef.current && inputRef.current.focus(); return; }
    const tokens = raw.split(",").map(t => t.trim()).filter(Boolean);
    const key = mode === "include" ? includeKey : excludeKey;
    const performAction = () => {
      const fd = dataSearchState.filterComponentData;
      const current = fd[key] || [];
      const newTokens = tokens.filter(t => !current.includes(t));
      if (!newTokens.length) { setInputValue(""); return; }
      dataSearchState.setFilterComponentData({ ...fd, [key]: [...current, ...newTokens] });
      setInputValue("");
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction, () => setInputValue(""));
    } else { performAction(); }
  };

  const handleRemove = (val, key) => {
    const performAction = () => {
      const fd = dataSearchState.filterComponentData;
      dataSearchState.setFilterComponentData({ ...fd, [key]: (fd[key] || []).filter(v => v !== val) });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const handleFlip = (val, fromKey) => {
    const toKey = fromKey === includeKey ? excludeKey : includeKey;
    const performAction = () => {
      const fd = dataSearchState.filterComponentData;
      const from = (fd[fromKey] || []).filter(v => v !== val);
      const to = [...new Set([...(fd[toKey] || []), val])];
      dataSearchState.setFilterComponentData({ ...fd, [fromKey]: from, [toKey]: to });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const handleClearAll = () => {
    const performAction = () => {
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        [includeKey]: [],
        [excludeKey]: []
      });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else { performAction(); }
  };

  const includeItems = dataSearchState.filterComponentData[includeKey] || [];
  const excludeItems = dataSearchState.filterComponentData[excludeKey] || [];
  const totalCount = includeItems.length + excludeItems.length;

  return (
    <>
      <div className="titleModeToggle">
        <button
          type="button"
          className={`titleModeBtn${mode === "include" ? " titleModeBtn--active titleModeBtn--include" : ""}`}
          onClick={() => setMode("include")}
        >
          Include
        </button>
        <button
          type="button"
          className={`titleModeBtn${mode === "exclude" ? " titleModeBtn--active titleModeBtn--exclude" : ""}`}
          onClick={() => setMode("exclude")}
        >
          Exclude
        </button>
      </div>
      <div className="filterComponentInputHeader">
        <InputBox
          name={includeKey}
          id={includeKey}
          type="text"
          value={inputValue}
          changeAction={setInputValue}
          keyupAction={e => { if (Utils.isSubmitKeyAction(e)) handleAdd(); }}
          blurAction={() => handleAdd()}
          label=""
          text=""
          placeholder={placeholder}
          inputBoxRef={inputRef}
          focus={true}
        />
        {totalCount > 0 && (
          <div className="flexCenter">
            <div className="filterCount">{totalCount}</div>
          </div>
        )}
      </div>
      <p className="filter-input-enter-hint">Press Enter to add</p>
      {totalCount > 0 && (
        <section className="addRemoveValue">
          <span className="horizontalBorderLine" />
          <div className="flexCenterWithJustifyContent">
            <div className="addRemoveValueWrapper titleChipsWrapper">
              {includeItems.map((val, idx) => (
                <div className="tagItem tagItem--include" key={`inc-${val}-${idx}`}>
                  <span className="selectedTag">{val}</span>
                  <Tooltip tooltip="Switch to Exclude" placement="top">
                    <i className="material-icons titleChipFlipIcon" onClick={() => handleFlip(val, includeKey)} role="button" aria-label="Switch to Exclude">swap_horiz</i>
                  </Tooltip>
                  <i className="material-icons selectedTagCloseIcon" onClick={() => handleRemove(val, includeKey)} role="button" aria-label={`Remove ${val}`}>close</i>
                </div>
              ))}
              {excludeItems.map((val, idx) => (
                <div className="tagItem tagItem--exclude" key={`exc-${val}-${idx}`}>
                  <span className="selectedTag">{val}</span>
                  <Tooltip tooltip="Switch to Include" placement="top">
                    <i className="material-icons titleChipFlipIcon" onClick={() => handleFlip(val, excludeKey)} role="button" aria-label="Switch to Include">swap_horiz</i>
                  </Tooltip>
                  <i className="material-icons selectedTagCloseIcon" onClick={() => handleRemove(val, excludeKey)} role="button" aria-label={`Remove ${val}`}>close</i>
                </div>
              ))}
            </div>
            <div className="clearAll">
              <Tooltip tooltip="Clear all" placement="left">
                <i className="material-icons clearAllIcon" onClick={handleClearAll}>clear_all</i>
              </Tooltip>
            </div>
          </div>
        </section>
      )}
    </>
  );
});

const FilterComponentAction = observer(props => {
  const {
    action = "",
    id = "",
    name = "",
    index = "",
    activeIndex = "",
    setActiveIndex = () => { },
    setTableLoading = () => { },
    filterCount = 0
  } = props;

  const [nameValue, setNameValue] = useState("");
  const inputBoxRef = createRef();

  const handleToggleAction = checked => {
    const performAction = () => {
      Utils.mixpanelTrack(MXP_EVENT.SKIP_CONTACTS_CLICKED, {
        pageType: PAGE_TYPE.discover,
        value: checked
      });
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        [id]: checked
      });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else {
      performAction();
    }
  };

  const handleInputChange = value => {
    setNameValue(value);
  };

  const handleKeyPress = (event, nameValue) => {
    if (Utils.isSubmitKeyAction(event)) {
      handleInputAdd(nameValue);
    }
  };

  const handleInputAdd = nameValue => {
    const raw = (nameValue || "").trim();
    if (raw.length > 0) {
      const performAction = () => {
        const tokens = raw.split(",").map(t => t.trim()).filter(Boolean);
        const current = dataSearchState?.filterComponentData?.[id] || [];
        const newTokens = tokens.filter(t => !current.includes(t));
        const combined = [...current, ...newTokens];
        if (newTokens.length > 0) {
          dataSearchState.setFilterComponentData({
            ...dataSearchState.filterComponentData,
            [id]: combined
          });
        }
        setNameValue("");
      };
      if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
        verifyBulkActionPerformedAndAllow(performAction, () =>
          setNameValue("")
        );
      } else {
        performAction();
      }
    } else {
      inputBoxRef && inputBoxRef.current && inputBoxRef.current.focus();
    }
  };

  const getActionComponent = () => {
    switch (action) {
      case "autoCompleteMultiSelectDropdown":
        return (
          <AutoCompleteMultiSelectDropdown
            {...props}
            discoverPage={true}
            showSearchPanel={true}
            showDropdown={index === activeIndex}
            setActiveIndex={setActiveIndex}
            showApplybtn={["keywords", "companykeywords", "contactkeywords", "location", "companydomain"].includes(
              id?.toLowerCase()
            )}
          />
        );
      case "jobFunctionDropdown":
        return (
          <JobFunctionDropdown
            {...props}
            showDropdown={index === activeIndex}
            setActiveIndex={setActiveIndex}
            discoverPage={true}
          />
        );
      case "fundingDropdown":
        return (
          <FundingDropdown
            {...props}
            showDropdown={index === activeIndex && (props.fundingTypeDropdownOpen !== false)}
            setActiveIndex={setActiveIndex}
            discoverPage={true}
            onCloseDropdownOnly={props.onCloseFundingTypeDropdown}
          />
        );
      case "multiSelectDropdown":
        return (
          <CustomMultiSelect
            {...props}
            showSearchPanel={false}
            showDropdown={index === activeIndex}
            setActiveIndex={setActiveIndex}
            discoverPage={true}
          />
        );
      case "toggle":
        return (
          <div className="toggleActionSection">
            <ToggleBtnType
              id={id}
              defaultValue={dataSearchState?.filterComponentData?.[id]}
              lastText={""}
              pageType={name}
              isAllowed={true}
              cbk={handleToggleAction}
            />
          </div>
        );
      case "companyDomainName":
        return (
          <CompanyDomainNameInputs inputBoxRef={inputBoxRef} />
        );
      case "contactInfo":
        return <ContactInfoInputs inputBoxRef={inputBoxRef} />;
      case "experienceDuration":
        return <ExperienceDurationInputsWrapper />;
      case "inputBox":
        const inputObject = {
          name: id,
          id: id,
          type: "text",
          value: nameValue || "",
          changeAction: handleInputChange,
          keyupAction: e => handleKeyPress(e, nameValue),
          blurAction: () => handleInputAdd(nameValue),
          label: "",
          text: "",
          placeholder: name,
          inputBoxRef,
          focus: true
        };

        return (
          <>
            <div className="filterComponentInputHeader">
              <InputBox {...inputObject} />
              {filterCount > 0 && (
                <div className="flexCenter">
                  <div className="filterCount">{filterCount}</div>
                </div>
              )}
            </div>
            <p className="filter-input-enter-hint">Press Enter to add</p>
          </>
        );
    }
  };
  return getActionComponent();
});

const FilterComponent = observer(props => {
  const {
    setTableLoading = () => { },
    setShowSavePopup = () => { },
    editCallBack = () => { },
    minimumSelectionMade = false,
    savedListOptions = [],
    setSavedListOptions = () => { },
    onSliderOpenChange = () => { },
    activeTab = "contacts"
  } = props;
  const isCompaniesTab = activeTab === "companies";

  const [checkboxVal, setCheckboxVal] = useState(
    dataSearchState.filterComponentData.titleExactMatch
  );
  const [activeIndex, setActiveIndex] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [savedSearchListLoader, setSavedSearchListLoader] = useState(false);
  const [selectedValueId, setSelectedValueId] = useState("");
  const [activeFilterIndex, setActiveFilterIndex] = useState(null);
  const [fundingTypeDropdownOpen, setFundingTypeDropdownOpen] = useState(false);
  const filterPanelWrapRef = useRef(null);

  const runSearchForActiveTab = async () => {
    if (dataSearchState.activeSearchTab === "companies") {
      return dataSearchState.applyFiltersAndSearch();
    }
    setTableLoading(true);
    const ran = await dataSearchState.applyFiltersAndSearch();
    if (!ran) setTableLoading(false);
    return ran;
  };

  const handleFilterPanelClose = async () => {
    setActiveFilterIndex(null);
    setActiveIndex(null);
    onSliderOpenChange(false);
    if (!dataSearchState.shouldAutoSearchOnFilterClose()) return;
    await runSearchForActiveTab();
  };

  useEffect(() => {
    onSliderOpenChange(activeFilterIndex != null);
  }, [activeFilterIndex, onSliderOpenChange]);

  // Close the right-side filter overlay when clicking outside it
  useEffect(() => {
    if (activeFilterIndex == null) return;
    const handleClickOutside = e => {
      // Don't close when interacting with a portal popup (e.g. paste popup) rendered in document.body
      if (e.target.closest(".cdnPasteOverlay")) return;
      // Don't close when clicking inside the funding date picker popper (it's portaled outside the panel)
      if (e.target.closest("#funding-datepicker-portal") || e.target.closest(".react-datepicker-popper.fundingDatePickerPopper")) return;
      if (filterPanelWrapRef.current && !filterPanelWrapRef.current.contains(e.target)) {
        setActiveFilterIndex(null);
        setActiveIndex(null);
        onSliderOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeFilterIndex, onSliderOpenChange]);

  useEffect(() => {
    if (dataSearchState.closeFilterSliderSignal > 0) {
      setActiveFilterIndex(null);
      setActiveIndex(null);
      onSliderOpenChange(false);
    }
  }, [dataSearchState.closeFilterSliderSignal]);

  useEffect(() => {
    const filterBody = document.getElementById("startSearchingItem");
    if (filterBody) {
      filterBody.scrollTop = 0;
    }
  }, []);

  const setSelectedValueCbk = (str = "") => {
    setSelectedValueId(str);
  };

  const getIndustryFilterCount = (id = "") => {
    let count = 0;
    if (dataSearchState?.industriesList?.length) {
      let tempData = dataSearchState?.industriesList?.forEach(item => {
        const { name = "", mapped_industries = [] } = item || {};
        if (dataSearchState?.filterComponentData?.[id]?.includes(name)) {
          count += mapped_industries?.length || 0;
        }
      });
    } else {
      count = dataSearchState?.filterComponentData?.[id]?.length || 0;
    }
    return (
      count + dataSearchState?.filterComponentData?.["sector"]?.length || 0
    );
  };

  const hasExperienceDurationSet = () => {
    const d = dataSearchState?.filterComponentData || {};
    const keys = Object.keys(DEFAULT_EXPERIENCE_DURATION);
    return keys.some(k => toNum(d[k]) > 0);
  };

  const hasFundingRangeSet = () => {
    const d = dataSearchState?.filterComponentData || {};
    const hasDuration = (d.fundingDurationStart != null && d.fundingDurationStart !== "") || (d.fundingDurationEnd != null && d.fundingDurationEnd !== "");
    const hasLastAmount = d.fundingLastAmountStart != null || d.fundingLastAmountEnd != null;
    const hasTotalAmount = d.fundingTotalAmountStart != null || d.fundingTotalAmountEnd != null;
    return hasDuration || hasLastAmount || hasTotalAmount;
  };

  const getFilterCount = (id = "") => {
    if (id === "experience") return hasExperienceDurationSet() ? 1 : 0;
    if (id === "funding") {
      const typesLen = (dataSearchState?.filterComponentData?.funding || []).length;
      const rangeCount = hasFundingRangeSet() ? 1 : 0;
      return typesLen + rangeCount;
    }
    if (id === "employeesByDepartment") {
      const arr = dataSearchState?.filterComponentData?.employeesByDepartment || [];
      return arr.filter(
        r => (r.function || []).length > 0 && (r.start != null || r.end != null)
      ).length;
    }
    if (id === "growthByDepartment") {
      const arr = dataSearchState?.filterComponentData?.growthByDepartment || [];
      return arr.filter(
        r =>
          (r.function || []).length > 0 &&
          (r.start != null || r.end != null || !!r.timeFrame)
      ).length;
    }
    if (id === "foundedYear") {
      const d = dataSearchState?.filterComponentData || {};
      const start = d.foundedYearStart;
      const end = d.foundedYearEnd;
      return start != null || end != null ? 1 : 0;
    }
    if (id === "growthByDepartment") {
      const arr = dataSearchState?.filterComponentData?.growthByDepartment || [];
      return arr.filter(
        r =>
          (r.function || []).length > 0 &&
          (r.start != null || r.end != null || !!r.timeFrame)
      ).length;
    }
    if (["companydomain", "location", "companylocation", "keywords", "companykeywords", "contactkeywords", "industry", "technologies"].includes(id?.toLowerCase())) {
      let extraExclude = 0;
      if (id?.toLowerCase() === "industry") extraExclude = dataSearchState?.selectedFilteredList?.industryExclude?.length ?? 0;
      else if (id?.toLowerCase() === "companykeywords") extraExclude = dataSearchState?.selectedFilteredList?.companyKeywordsExclude?.length ?? 0;
      else if (id?.toLowerCase() === "contactkeywords") extraExclude = dataSearchState?.selectedFilteredList?.contactKeywordsExclude?.length ?? 0;
      const listCount = (dataSearchState?.selectedFilteredList?.[id]?.length ?? 0) + extraExclude;
      const dataCount = dataSearchState?.filterComponentData?.[id]?.length;
      if (id?.toLowerCase() === "companydomain") {
        const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
        const fd = dataSearchState?.filterComponentData || {};
        if (st === "name") return fd.companyName?.length ?? 0;
        if (st === "domainList") return fd.companyDomainList?.length > 0 ? 1 : 0;
        if (st === "nameList") return fd.companyNameList?.length > 0 ? 1 : 0;
        if (st === "linkedin") return fd.companyLinkedIn?.length ?? 0;
        if (st === "linkedinList") return fd.companyLinkedInList?.length > 0 ? 1 : 0;
        return fd.companyDomain?.length ?? listCount ?? 0;
      }
      return listCount ?? 0;
    }
    if (id === "skill") {
      const inc = (dataSearchState?.filterComponentData?.skill || []).length;
      const exc = (dataSearchState?.filterComponentData?.skillExclude || []).length;
      return inc + exc;
    }
    if (id === "certification") {
      const inc = (dataSearchState?.filterComponentData?.certification || []).length;
      const exc = (dataSearchState?.filterComponentData?.certificationExclude || []).length;
      return inc + exc;
    }
    if (id === "name") {
      const fd = dataSearchState?.filterComponentData || {};
      const st = fd.contactSearchType || "name";
      if (st === "linkedin") return (fd.contactLinkedIn || []).length;
      const n = fd.name;
      return Array.isArray(n) ? n.length : typeof n === "string" && n.trim() ? 1 : 0;
    }
    if (id === "excludeContacts") {
      const fd = dataSearchState?.filterComponentData || {};
      const hasListSelection = !!(fd.excludeContactListName || fd.excludeContactListId);
      const urlsCount = (fd.excludeContactLinkedIn || []).length;
      return hasListSelection ? Math.max(1, urlsCount) : urlsCount;
    }
    return dataSearchState?.filterComponentData?.[id]?.length;
  };

  const getActiveFiltersSummary = () => {
    const defaults = getDefaultFilterState();
    const items = [];
    [...companyFilterData, ...contactFilterData].forEach(def => {
      const id = def?.id;
      const name = def?.name;
      const val = dataSearchState?.filterComponentData?.[id];
      const listVal = dataSearchState?.selectedFilteredList?.[id];
      const count = getFilterCount(id);
      if (count === undefined || count === 0) return;
      if (id === "titleExactMatch" || id === "excludeTeamOwns") return;
      items.push({ id, label: name, count });
    });
    return items;
  };

  const clearSingleFilter = (id = "") => {
    const defaults = getDefaultFilterState();
    const defaultVal = defaults[id];
    const performClear = () => {
      const nextFilter = id === "experience"
        ? { ...dataSearchState.filterComponentData, ...DEFAULT_EXPERIENCE_DURATION }
        : { ...dataSearchState.filterComponentData, [id]: defaultVal };
      if (id === "funding") {
        nextFilter.fundingTotalAmountStart = null;
        nextFilter.fundingTotalAmountEnd = null;
        nextFilter.fundingLastAmountStart = null;
        nextFilter.fundingLastAmountEnd = null;
        nextFilter.fundingDurationStart = null;
        nextFilter.fundingDurationEnd = null;
      }
      if (id === "employeesByDepartment") {
        nextFilter.employeesByDepartment = [];
        dataSearchState.setEmployeesByDepartmentErrors({});
      }
      if (id === "growthByDepartment") {
        nextFilter.growthByDepartment = [];
        dataSearchState.setGrowthByDepartmentErrors({});
      }
      if (id === "foundedYear") {
        nextFilter.foundedYearStart = null;
        nextFilter.foundedYearEnd = null;
      }
      if (id?.toLowerCase() === "industry") {
        nextFilter.sector = [];
        nextFilter.industryExclude = [];
      }
      if (id?.toLowerCase() === "companykeywords") {
        nextFilter.companyKeywordsExclude = [];
      }
      if (id?.toLowerCase() === "contactkeywords") {
        nextFilter.contactKeywordsExclude = [];
      }
      if (id === "skill") nextFilter.skillExclude = [];
      if (id === "certification") nextFilter.certificationExclude = [];
      if (id === "name") {
        nextFilter.name = Array.isArray(defaults.name) ? defaults.name : [];
        nextFilter.contactLinkedIn = Array.isArray(defaults.contactLinkedIn) ? defaults.contactLinkedIn : [];
        nextFilter.contactSearchType = defaults.contactSearchType || "name";
      }
      if (id === "excludeContacts") {
        nextFilter.excludeContactsMode = defaults.excludeContactsMode || "list";
        nextFilter.excludeContactListId = null;
        nextFilter.excludeContactListName = "";
        nextFilter.excludeContactLinkedIn = [];
      }
      if (id?.toLowerCase() === "companydomain") {
        nextFilter.companyName = [];
        nextFilter.companyDomainList = [];
        nextFilter.companyNameList = [];
        nextFilter.companyLinkedIn = [];
        nextFilter.companyLinkedInList = [];
        nextFilter.companySearchType = "domain";
      }
      dataSearchState.setFilterComponentData(nextFilter);
      if (["companydomain", "location", "companylocation", "companykeywords", "contactkeywords", "industry", "technologies"].includes(id?.toLowerCase())) {
        const updatedSFL = { ...dataSearchState.selectedFilteredList, [id]: [] };
        if (id?.toLowerCase() === "industry") updatedSFL.industryExclude = [];
        if (id?.toLowerCase() === "companykeywords") updatedSFL.companyKeywordsExclude = [];
        if (id?.toLowerCase() === "contactkeywords") updatedSFL.contactKeywordsExclude = [];
        dataSearchState.setSelectedFilteredList(updatedSFL);
      }
      if (id === "level" || id === "confidenceScore" || id === "profileBadge") resetFilterData(id, false);
      // Do not refresh or reset page — just clear selection; stale overlay will prompt user to refresh
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performClear);
    } else {
      performClear();
    }
  };

  const resetFilterData = (id = "", checked = false) => {
    if (id) {
      filterComponentData.map(item => {
        if (item?.id === id) {
          item.options.map(options => {
            options.checked = checked;
          });
        }
      });
    }
  };

  const getDefaultFilterState = () => ({
    confidenceScore: [],
    excludeTeamOwns: true,
    industry: [],
    industryExclude: [],
    sector: [],
    employeeCount: [],
    revenue: [],
    department: [],
    funding: [],
    fundingTotalAmountStart: null,
    fundingTotalAmountEnd: null,
    fundingLastAmountStart: null,
    fundingLastAmountEnd: null,
    fundingDurationStart: null,
    fundingDurationEnd: null,
    employeesByDepartment: [],
    growthByDepartment: [],
    companyType: [],
    foundedYearStart: null,
    foundedYearEnd: null,
    companyType: [],
    foundedYearStart: null,
    foundedYearEnd: null,
    technologies: [],
    level: [],
    title: [],
    titleExclude: [],
    skill: [],
    skillExclude: [],
    certification: [],
    certificationExclude: [],
    titleExactMatch: false,
    primaryActiveRoleOnly: true,
    companyDomain: [],
    companyName: [],
    companyDomainList: [],
    companyNameList: [],
    companyLinkedIn: [],
    companyLinkedInList: [],
    companySearchType: "domain",
    companyKeywords: [],
    companyKeywordsExclude: [],
    contactKeywords: [],
    contactKeywordsExclude: [],
    profileBadge: [],
    ...DEFAULT_EXPERIENCE_DURATION,
    country: [],
    state: [],
    city: [],
    companyCountry: [],
    companyState: [],
    companyCity: [],
    name: [],
    contactLinkedIn: [],
    contactSearchType: "name",
    excludeContactsMode: "list",
    excludeContactListId: null,
    excludeContactListName: "",
    excludeContactLinkedIn: []
  });

  const clearAllAction = () => {
    setSelectedValueId("");
    setCheckboxVal(false);
    const performClearAll = () => {
      Utils.mixpanelTrack(MXP_EVENT.CLEAR_ALL_CLICKED, {
        pageType: PAGE_TYPE.discover
      });
      dataSearchState.setClearAll(true);
      dataSearchState.setFilterComponentData(getDefaultFilterState());
      dataSearchState.setSelectedFilteredList({});
      dataSearchState.setCurrentPage(1);
      setTableLoading(true);
      dataSearchState.setExperienceDurationErrors({});
      dataSearchState.setEmployeesByDepartmentErrors({});
      dataSearchState.setGrowthByDepartmentErrors({});
      resetFilterData("level");
      resetFilterData("confidenceScore");
      dataSearchState.setIsFromSeoPage(false);
      dataSearchState.setShowInitialPage(true);
      dataSearchState.setFilterApplied(false);
      dataSearchState.setContactTableData({
        totalContacts: 0
      });
      dataSearchState.setLastSearchedFilters(null);
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performClearAll);
    } else {
      performClearAll();
    }
  };

  const getListOfSavedSearch = () => {
    const config = {
      url: URLS.getSavedSearchV2,
      method: "GET"
    };
    return makeApi(config);
  };

  const deleteSavedSearch = id => {
    const config = {
      url: URLS.deleteSearchV2,
      method: "POST",
      data: { id }
    };
    return makeApi(config);
  };

  const handleGetSearch = async () => {
    const willOpen = !showDropdown;
    if (willOpen) {
      setSavedSearchListLoader(true);
      let response = await getListOfSavedSearch();
      if (response?.status === 200) {
        const raw = response?.data;
        const list = Array.isArray(raw)
          ? raw
          : raw?.content ?? raw?.data ?? raw?.savedSearches ?? [];
        setSavedListOptions(Array.isArray(list) ? list : []);
      }
      setSavedSearchListLoader(false);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleDeleteSearch = async (id = "") => {
    let response = await deleteSavedSearch(id);
    if (response?.status === 200) {
      Utils.mixpanelTrack(MXP_EVENT.SEARCH_DELETED, {
        pageType: PAGE_TYPE.discover
      });
      toasterState.setToastMsg("Saved search deleted successfully", "success");
      let temp = savedListOptions.filter(item => item.id !== id);
      setSavedListOptions(temp);
    } else {
      toasterState.setToastMsg(
        "Deletion was unsuccussful,please try again later",
        "success"
      );
    }
    if (
      id === selectedValueId ||
      (setSavedListOptions &&
        setSavedListOptions?.length === 1 &&
        savedListOptions[0].id === id)
    ) {
      clearAllAction();
    }
    confirmationPopupState.setShowPopup(false);
    setShowDropdown(false);
  };

  const handleDeletePopup = (id = "") => {
    confirmationPopupState.setPopupValues({
      content: `Are you sure you want to delete the saved search?`,
      actionBtnText: "Confirm",
      cancelBtnText: "Cancel",
      loadingMsg: "Please wait...",
      callback: () => {
        handleDeleteSearch(id);
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  const handleEditSearch = (id = "", searchName = "") => {
    editCallBack(id, searchName);
    setShowDropdown(false);
  };

  const listOfSavedSearchBody = props => {
    const {
      searchName = "",
      createTs = "",
      id = "",
      dropDownValue = ""
    } = props;
    const createdDate = Utils.formatDate(createTs);
    let tempDropDownValue = dataSearchState.clearAll ? "" : selectedValueId;

    return (
      <>
        <div className="customListLeftSection">
          <span
            className={`${id === tempDropDownValue ? "updatedName" : "nonUpdatedName"
              }`}
          >
            {searchName}
          </span>
          <span className="createdDate">{`Created on ${createdDate}`}</span>
        </div>
        <div className="customListHorizontalDisplay">
          <span
            onClick={e => {
              Utils.preventDefaultFn(e);
              handleEditSearch(id, searchName);
            }}
          >
            <EditIcon />
          </span>
          <span
            onClick={e => {
              Utils.preventDefaultFn(e);
              handleDeletePopup(id);
            }}
          >
            <DeleteIconDiscover />
          </span>
        </div>
      </>
    );
  };

  const CustomSavedSearchLoader = () => {
    return Array.from({ length: 10 }, (value, index) => (
      <li className="dropdownOptions optionLoader" key={`${index}`}>
        <>
          <div className="customListLeftSection">
            <span
              className="updatedName linear-background"
              style={{ width: "150px" }}
            />
            <span
              className="createdDate linear-background"
              style={{ width: "150px" }}
            />
          </div>
          <div className="customListHorizontalDisplay">
            <span
              className="iconBackground linear-background"
              style={{ width: "20px" }}
            />
            <span
              className="iconBackground linear-background"
              style={{ width: "20px" }}
            />
          </div>
        </>
      </li>
    ));
  };

  const LoaderHeader = () => {
    return (
      <span
        className="filterHeaderLink"
        onClick={() => {
          handleGetSearch();
        }}
      >
        Load
      </span>
    );
  };

  const savedListCbk = item => {
    setTableLoading(true);
    const {
      confidenceScore = [],
      titleExactMatch = false,
      titleExclude = [],
      industry = [],
      industryExclude = [],
      sector = [],
      employeeCount = [],
      revenue = [],
      department = [],
      level = [],
      title = [],
      excludeTeamOwns = false,
      keywords = [],
      companyKeywords = [],
      companyKeywordsExclude = [],
      contactKeywords = [],
      contactKeywordsExclude = [],
      skill = [],
      skillExclude = [],
      certification = [],
      certificationExclude = [],
      profileBadge = [],
      experienceCurrentJobMinYear,
      experienceCurrentJobMaxYear,
      experienceCurrentJobMinMonth,
      experienceCurrentJobMaxMonth,
      experienceCurrentCompanyMinYear,
      experienceCurrentCompanyMaxYear,
      experienceCurrentCompanyMinMonth,
      experienceCurrentCompanyMaxMonth,
      experienceTotalMinYear,
      experienceTotalMaxYear,
      experienceTotalMinMonth,
      experienceTotalMaxMonth,
      companyDomain: rawCompanyDomain = [],
      companyName: rawCompanyName = [],
      companyDomainList: rawCompanyDomainList = [],
      companyNameList: rawCompanyNameList = [],
      companyLinkedIn: rawCompanyLinkedIn = [],
      companyLinkedInList: rawCompanyLinkedInList = [],
      companySearchType = "domain",
      funding = [],
      fundingTotalAmountStart = null,
      fundingTotalAmountEnd = null,
      fundingLastAmountStart = null,
      fundingLastAmountEnd = null,
      fundingDurationStart = null,
      fundingDurationEnd = null,
      country = [],
      state = [],
      city = [],
      companyCountry = [],
      companyState = [],
      companyCity = [],
      name: rawSavedName = "",
      contactLinkedIn: rawSavedContactLinkedIn = [],
      contactSearchType: rawSavedContactSearchType = "name",
      excludeContactsMode: rawExcludeContactsMode = "list",
      excludeContactListId: rawExcludeContactListId = null,
      excludeContactListName: rawExcludeContactListName = "",
      excludeContactLinkedIn: rawExcludeContactLinkedIn = [],
      companies = [],
      locations = [],
      companyLocations = [],
      technologies = [],
      companyType = [],
      foundedYearStart = null,
      foundedYearEnd = null,
      employeesByDepartment = [],
      growthByDepartment = [],
      primaryActiveRoleOnly = true
    } = item;
    const companyDomain = (() => {
      const raw = rawCompanyDomain;
      if (Array.isArray(raw)) {
        return raw.map(v =>
          typeof v === "object" && v != null
            ? (v.primaryDomain || v.name || v.id || "")
            : String(v ?? "")
        ).filter(Boolean);
      }
      if (raw != null && raw !== "") return [String(raw)];
      return [];
    })();
    const companyName = (() => {
      const raw = rawCompanyName;
      if (Array.isArray(raw)) {
        return raw.map(v => (typeof v === "object" && v != null ? (v.name || v.id || "") : String(v ?? ""))).filter(Boolean);
      }
      if (raw != null && raw !== "") return [String(raw)];
      return [];
    })();
    const companyDomainList = Array.isArray(rawCompanyDomainList) ? rawCompanyDomainList.map(v => (typeof v === "object" && v != null ? String(v?.id ?? v?.name ?? "") : String(v ?? ""))).filter(Boolean) : [];
    const companyNameList = Array.isArray(rawCompanyNameList) ? rawCompanyNameList.map(v => (typeof v === "object" && v != null ? String(v?.name ?? v?.id ?? "") : String(v ?? ""))).filter(Boolean) : [];
    const companyLinkedIn = (() => {
      const raw = rawCompanyLinkedIn;
      if (Array.isArray(raw)) {
        return raw.map(v => (typeof v === "object" && v != null ? String(v?.name ?? v?.id ?? "") : String(v ?? ""))).filter(Boolean);
      }
      if (raw != null && raw !== "") return [String(raw)];
      return [];
    })();
    const companyLinkedInList = Array.isArray(rawCompanyLinkedInList)
      ? rawCompanyLinkedInList.map(v => (typeof v === "object" && v != null ? String(v?.name ?? v?.id ?? "") : String(v ?? ""))).filter(Boolean)
      : [];
    const savedNameArr = (() => {
      const raw = rawSavedName;
      if (Array.isArray(raw)) {
        return raw.map(v => String(v ?? "").trim()).filter(Boolean);
      }
      if (raw != null && String(raw).trim()) return [String(raw).trim()];
      return [];
    })();
    const savedContactLinkedInArr = Array.isArray(rawSavedContactLinkedIn)
      ? rawSavedContactLinkedIn.map(v => String(v ?? "").trim()).filter(Boolean)
      : [];
    const savedExcludeContactLinkedIn = Array.isArray(rawExcludeContactLinkedIn)
      ? [...new Set(rawExcludeContactLinkedIn.map(v => normalizeLinkedInProfileUrl(v)).filter(Boolean))].slice(0, MAX_EXCLUDE_CONTACT_URLS)
      : [];
    const savedContactSearchType =
      ["name", "linkedin"].includes(String(rawSavedContactSearchType || "").toLowerCase())
        ? String(rawSavedContactSearchType).toLowerCase()
        : savedContactLinkedInArr.length > 0 && savedNameArr.length === 0
          ? "linkedin"
          : "name";
    let filterAttr = {
      confidenceScore,
      titleExactMatch,
      titleExclude: Array.isArray(titleExclude) ? titleExclude : [],
      industry,
      industryExclude: Array.isArray(industryExclude) ? industryExclude : [],
      sector,
      employeeCount,
      revenue,
      department,
      level,
      title,
      excludeTeamOwns,
      companyDomain,
      companyName,
      companyDomainList,
      companyNameList,
      companyLinkedIn,
      companyLinkedInList,
      companySearchType: ["domain", "name", "domainList", "nameList", "linkedin", "linkedinList"].includes(companySearchType)
        ? companySearchType
        : "domain",
      funding: Array.isArray(funding) ? funding : [],
      fundingTotalAmountStart: fundingTotalAmountStart != null ? fundingTotalAmountStart : null,
      fundingTotalAmountEnd: fundingTotalAmountEnd != null ? fundingTotalAmountEnd : null,
      fundingLastAmountStart: fundingLastAmountStart != null ? fundingLastAmountStart : null,
      fundingLastAmountEnd: fundingLastAmountEnd != null ? fundingLastAmountEnd : null,
      fundingDurationStart: fundingDurationStart != null && fundingDurationStart !== "" ? fundingDurationStart : null,
      fundingDurationEnd: fundingDurationEnd != null && fundingDurationEnd !== "" ? fundingDurationEnd : null,
      companyKeywords: companyKeywords?.length ? companyKeywords : (keywords?.length ? keywords : []),
      companyKeywordsExclude: Array.isArray(companyKeywordsExclude) ? companyKeywordsExclude : [],
      contactKeywords: contactKeywords?.length ? contactKeywords : (keywords?.length ? keywords : []),
      contactKeywordsExclude: Array.isArray(contactKeywordsExclude) ? contactKeywordsExclude : [],
      skill: Array.isArray(skill) ? skill : [],
      skillExclude: Array.isArray(skillExclude) ? skillExclude : [],
      certification: Array.isArray(certification) ? certification : [],
      certificationExclude: Array.isArray(certificationExclude) ? certificationExclude : [],
      profileBadge: profileBadge?.length ? profileBadge : [],
      ...Object.fromEntries(
        Object.keys(DEFAULT_EXPERIENCE_DURATION).map(k => {
          const v = {
            experienceCurrentJobMinYear,
            experienceCurrentJobMaxYear,
            experienceCurrentJobMinMonth,
            experienceCurrentJobMaxMonth,
            experienceCurrentCompanyMinYear,
            experienceCurrentCompanyMaxYear,
            experienceCurrentCompanyMinMonth,
            experienceCurrentCompanyMaxMonth,
            experienceTotalMinYear,
            experienceTotalMaxYear,
            experienceTotalMinMonth,
            experienceTotalMaxMonth
          }[k];
          return [k, v != null && Number(v) >= 0 ? toNum(v) : DEFAULT_EXPERIENCE_DURATION[k]];
        })
      ),
      country,
      state,
      city,
      companyCountry,
      companyState,
      companyCity,
      name: savedContactSearchType === "linkedin" ? [] : savedNameArr,
      contactLinkedIn: savedContactSearchType === "name" ? [] : savedContactLinkedInArr,
      contactSearchType: savedContactSearchType,
      excludeContactsMode: rawExcludeContactsMode === "urls" ? "urls" : "list",
      excludeContactListId: rawExcludeContactListId || null,
      excludeContactListName: rawExcludeContactListName || "",
      excludeContactLinkedIn: savedExcludeContactLinkedIn,
      technologies: Array.isArray(technologies) ? technologies : [],
      companyType: Array.isArray(companyType) ? companyType : [],
      foundedYearStart: foundedYearStart != null ? foundedYearStart : null,
      foundedYearEnd: foundedYearEnd != null ? foundedYearEnd : null,
      employeesByDepartment: Array.isArray(employeesByDepartment) ? employeesByDepartment : [],
      growthByDepartment: Array.isArray(growthByDepartment) ? growthByDepartment : [],
      primaryActiveRoleOnly: typeof primaryActiveRoleOnly === "boolean" ? primaryActiveRoleOnly : true
    };
    dataSearchState.setSelectedFilteredList({
      location: [],
      companyDomain: [],
      companyLocation: [],
      companyKeywords: [],
      companyKeywordsExclude: [],
      contactKeywords: [],
      contactKeywordsExclude: []
    });
    dataSearchState.setFilterComponentData({
      ...filterAttr
    });
    if (dataSearchState.filterComponentData.titleExactMatch) {
      setCheckboxVal(true);
    }
    let tempCompanyArr = [];
    if (companies && companies?.length > 0) {
      for (const iterator of companies) {
        tempCompanyArr.push({ ...iterator, checked: true });
      }
    }
    let tempLocationArr = [];
    if (locations && locations?.length > 0) {
      for (const iterator of locations) {
        tempLocationArr.push({ ...iterator, checked: true });
      }
    }
    let tempCompanyLocationArr = [];
    if (companyLocations && companyLocations?.length > 0) {
      for (const iterator of companyLocations) {
        tempCompanyLocationArr.push({ ...iterator, checked: true });
      }
    }
    const toKeywordChips = (arr, id) =>
      (arr || []).map(iterator => ({
        name: typeof iterator === "string" ? iterator : iterator?.name ?? iterator,
        checked: true,
        id: typeof iterator === "string" ? iterator : iterator?.id ?? iterator,
        group: "keywords",
        searchText: Array.isArray(iterator?.searchText) ? iterator.searchText : [typeof iterator === "string" ? iterator : iterator?.name ?? iterator]
      }));
    const tempCompanyKeywordsArr = toKeywordChips(companyKeywords?.length ? companyKeywords : keywords, "companyKeywords");
    const tempContactKeywordsArr = toKeywordChips(contactKeywords?.length ? contactKeywords : keywords, "contactKeywords");
    const tempCompanyKeywordsExcludeArr = toKeywordChips(companyKeywordsExclude, "companyKeywordsExclude");
    const tempContactKeywordsExcludeArr = toKeywordChips(contactKeywordsExclude, "contactKeywordsExclude");
    const toNameChips = arr =>
      (Array.isArray(arr) ? arr : []).map(name => ({
        id: typeof name === "string" ? name.toLowerCase() : String(name),
        name: typeof name === "string" ? name : String(name),
        checked: true
      }));
    const toIdChips = arr =>
      (Array.isArray(arr) ? arr : []).map(id => ({
        id: typeof id === "string" ? id : String(id),
        name: typeof id === "string" ? id.replace(/\b\w/g, l => l.toUpperCase()) : String(id),
        checked: true
      }));
    dataSearchState.setSelectedFilteredList({
      ...dataSearchState.selectedFilteredList,
      companyDomain: tempCompanyArr,
      location: tempLocationArr,
      companyLocation: tempCompanyLocationArr,
      companyKeywords: tempCompanyKeywordsArr,
      companyKeywordsExclude: tempCompanyKeywordsExcludeArr,
      contactKeywords: tempContactKeywordsArr,
      contactKeywordsExclude: tempContactKeywordsExcludeArr,
      industry: toNameChips(industry),
      industryExclude: toNameChips(industryExclude),
      technologies: toIdChips(technologies)
    });
    setShowDropdown(false);
    dataSearchState.setCurrentPage(1);
    dataSearchState.setCompanyCurrentPage(1);
    dataSearchState.setIsFromSeoPage(false);
    if (dataSearchState.activeSearchTab === "companies") {
      dataSearchState.searchCompanyData();
    } else {
      dataSearchState.searchPageData();
    }
  };

  return (
    <section className="filterComponentV2Wrapper">
      <div className="filterComponentHeader filterComponentHeaderLoadSave">
        <section className="customListWrapper">
          <div className="customListContainer">
            <AutoCompleteDropdown
              HeaderChildren={<LoaderHeader />}
              optionList={savedListOptions}
              cbk={savedListCbk}
              loader={savedSearchListLoader}
              updateListBody={true}
              Body={listOfSavedSearchBody}
              loaderComponent={<CustomSavedSearchLoader />}
              showDropdown={showDropdown}
              setShowDropdown={setShowDropdown}
              discoverPage={true}
              setSelectedValueCbk={setSelectedValueCbk}
              handleGetSearch={handleGetSearch}
            />
          </div>
        </section>
        <span className="filterHeaderSeparator">|</span>
        <span
          className="filterHeaderLink"
          onClick={() => {
            if (minimumSelectionMade) {
              toasterState.setToastMsg(
                "Oops! You cannot save a search without applying any filters.",
                "failure"
              );
            } else {
              setShowSavePopup(true);
            }
          }}
        >
          Save
        </span>
        <span className="filterHeaderSeparator">|</span>
        <span
          className="filterHeaderLink"
          onClick={clearAllAction}
        >
          Clear All
        </span>
      </div>

      <div className="filterComponentBody filterComponentBodyListSlider" id="startSearchingItem">
        <div className="filterListLeft">
          <div className="filterSectionHeader filterListSectionHeader">Company</div>
          {companyFilterData.map((item, index) => {
            const { name = "", id = "", icon = "" } = item;
            const count = getFilterCount(id);
            const isActive = activeFilterIndex === index;
            const performSelect = () => {
              if (isActive) {
                setActiveFilterIndex(null);
                setActiveIndex(null);
                return;
              }
              if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
                verifyBulkActionPerformedAndAllow(() => {
                  setActiveFilterIndex(index);
                  setActiveIndex(index);
                });
              } else {
                setActiveFilterIndex(index);
                setActiveIndex(index);
              }
            };
            const includeCount = id === "title" ? (dataSearchState?.filterComponentData?.title || []).length : id === "industry" ? (dataSearchState?.selectedFilteredList?.industry || []).length : id === "skill" ? (dataSearchState?.filterComponentData?.skill || []).length : id === "certification" ? (dataSearchState?.filterComponentData?.certification || []).length : null;
            const excludeCount = id === "title" ? (dataSearchState?.filterComponentData?.titleExclude || []).length : id === "industry" ? (dataSearchState?.selectedFilteredList?.industryExclude || []).length : id === "skill" ? (dataSearchState?.filterComponentData?.skillExclude || []).length : id === "certification" ? (dataSearchState?.filterComponentData?.certificationExclude || []).length : null;
            return (
              <div
                key={`${id}_company`}
                className={`filterListItem ${isActive ? "isActive" : ""}`}
                onClick={performSelect}
                onKeyDown={e => e.key === "Enter" && performSelect()}
                role="button"
                tabIndex={0}
              >
                {icon && <span className="material-icons filterListItemIcon">{icon}</span>}
                <span className="filterListItemName">{name}</span>
                {(id === "title" || id === "industry" || id === "skill" || id === "certification") ? (
                  <>
                    {includeCount > 0 && <span className="filterListItemCount">{includeCount}</span>}
                    {excludeCount > 0 && <span className="filterListItemCount filterListItemCount--exclude">{excludeCount}</span>}
                  </>
                ) : (
                  count > 0 && <span className="filterListItemCount">{count}</span>
                )}
              </div>
            );
          })}
          <div className={`filterContactSection ${isCompaniesTab ? "filterContactSection--disabled" : ""}`}>
            <div className="filterSectionHeader filterListSectionHeader">
              Contact
              {isCompaniesTab && <span className="filterSectionDisabledHint">Active on Contacts tab</span>}
            </div>
            {contactFilterData.map((item, index) => {
              const idx = companyFilterData.length + index;
              const { name = "", id = "", icon = "" } = item;
              const count = getFilterCount(id);
              const isActive = activeFilterIndex === idx;
              const performSelect = () => {
                if (isCompaniesTab) return;
                if (isActive) {
                  setActiveFilterIndex(null);
                  setActiveIndex(null);
                  return;
                }
                if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
                  verifyBulkActionPerformedAndAllow(() => {
                    setActiveFilterIndex(idx);
                    setActiveIndex(idx);
                  });
                } else {
                  setActiveFilterIndex(idx);
                  setActiveIndex(idx);
                }
              };
              const includeCount = id === "title" ? (dataSearchState?.filterComponentData?.title || []).length : id === "industry" ? (dataSearchState?.selectedFilteredList?.industry || []).length : id === "skill" ? (dataSearchState?.filterComponentData?.skill || []).length : id === "certification" ? (dataSearchState?.filterComponentData?.certification || []).length : null;
              const excludeCount = id === "title" ? (dataSearchState?.filterComponentData?.titleExclude || []).length : id === "industry" ? (dataSearchState?.selectedFilteredList?.industryExclude || []).length : id === "skill" ? (dataSearchState?.filterComponentData?.skillExclude || []).length : id === "certification" ? (dataSearchState?.filterComponentData?.certificationExclude || []).length : null;
              return (
                <div
                  key={`${item.id}_contact`}
                  className={`filterListItem ${isActive ? "isActive" : ""} ${isCompaniesTab ? "filterListItem--muted" : ""}`}
                  onClick={performSelect}
                  onKeyDown={e => e.key === "Enter" && performSelect()}
                  role="button"
                  tabIndex={isCompaniesTab ? -1 : 0}
                >
                  {icon && <span className="material-icons filterListItemIcon">{icon}</span>}
                  <span className="filterListItemName">{name}</span>
                  {(id === "title" || id === "industry" || id === "skill" || id === "certification") ? (
                    <>
                      {includeCount > 0 && <span className="filterListItemCount">{includeCount}</span>}
                      {excludeCount > 0 && <span className="filterListItemCount filterListItemCount--exclude">{excludeCount}</span>}
                    </>
                  ) : (
                    count > 0 && <span className="filterListItemCount">{count}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {activeFilterIndex != null && (() => {
          const _allFilters = [...companyFilterData, ...contactFilterData];
          const _item = _allFilters[activeFilterIndex];
          const _isAccordion = _item && ["jobFunctionDropdown", "fundingDropdown"].includes(_item.action);
          const _isExpandable = _item && ["title", "companykeywords", "contactkeywords", "skill", "certification"].includes((_item.id || "").toLowerCase());
          const _isSimpleList = _item && _item.action === "multiSelectDropdown";
          return (
        <div ref={filterPanelWrapRef} className={`filter-panel-wrap${_isAccordion ? " filter-panel-wrap--accordion" : ""}${_isExpandable ? " filter-panel-wrap--expandable" : ""}${_isSimpleList ? " filter-panel-wrap--simple-list" : ""}`} key={activeFilterIndex}>
          {(() => {
            const allFilters = [...companyFilterData, ...contactFilterData];
            const item = allFilters[activeFilterIndex];
            if (!item) return null;
            const { name = "", action = "", id = "" } = item;
            const filterCount = getFilterCount(id);
            const isDropdown = ["multiSelectDropdown", "jobFunctionDropdown", "fundingDropdown", "autoCompleteMultiSelectDropdown"].includes(action);

            const help = FILTER_FIELD_HELP[id];
            const fd = dataSearchState.filterComponentData || {};
            const sl = dataSearchState.selectedFilteredList || {};
            let hasValue = false;
            if (id === "title") hasValue = (fd.title || []).length > 0 || (fd.titleExclude || []).length > 0;
            else if (id === "industry") hasValue = (sl.industry || []).length > 0 || (sl.industryExclude || []).length > 0;
            else if (id === "companyDomain") {
              const st = fd.companySearchType || "domain";
              hasValue =
                (st === "domain" && (fd.companyDomain || []).length > 0) ||
                (st === "name" && (fd.companyName || []).length > 0) ||
                (st === "domainList" && (fd.companyDomainList || []).length > 0) ||
                (st === "nameList" && (fd.companyNameList || []).length > 0) ||
                (st === "linkedin" && (fd.companyLinkedIn || []).length > 0) ||
                (st === "linkedinList" && (fd.companyLinkedInList || []).length > 0);
            } else if (id === "companyLocation") hasValue = (sl.companyLocation || []).length > 0;
            else if (id === "foundedYear") hasValue = fd.foundedYearStart != null || fd.foundedYearEnd != null;
            else if (id === "companyKeywords") hasValue = (fd.companyKeywords || []).length > 0 || (fd.companyKeywordsExclude || []).length > 0;
            else if (id === "contactKeywords") hasValue = (fd.contactKeywords || []).length > 0 || (fd.contactKeywordsExclude || []).length > 0;
            else if (id === "skill") hasValue = (fd.skill || []).length > 0 || (fd.skillExclude || []).length > 0;
            else if (id === "certification") hasValue = (fd.certification || []).length > 0 || (fd.certificationExclude || []).length > 0;
            else if (id === "name") {
              const st = fd.contactSearchType || "name";
              if (st === "linkedin") {
                hasValue = (fd.contactLinkedIn || []).length > 0;
              } else {
                const n = fd.name;
                hasValue = Array.isArray(n) ? n.length > 0 : typeof n === "string" && n.trim().length > 0;
              }
            }
            else if (id === "excludeContacts") hasValue = (fd.excludeContactLinkedIn || []).length > 0;
            else if (id === "location") hasValue = (sl.location || []).length > 0;
            const searchQuery = (dataSearchState.filterPanelSearchQuery || {})[id];
            const hasSearchInput = typeof searchQuery === "string" && searchQuery.trim().length > 0;
            const helpContent = help && !hasValue && !hasSearchInput ? (
              <p className="filter-panel__field-hint-text">{help.description} <span className="filter-panel__field-hint-example">{help.example}</span></p>
            ) : null;

            if (action === "multiSelectDropdown") {
              return (
                <FilterPanelOverlay
                  title={name}
                  onClear={() => clearSingleFilter(id)}
                  onClose={handleFilterPanelClose}
                  showClear={filterCount > 0}
                  helpContent={helpContent}
                >
                  <FilterPanelCheckboxList id={id} options={item.options} />
                </FilterPanelOverlay>
              );
            }

            return (
              <FilterPanelOverlay
                title={name}
                onClear={() => clearSingleFilter(id)}
                onClose={handleFilterPanelClose}
                showClear={filterCount > 0}
                helpContent={helpContent}
                panelClassName={["industry", "technologies"].includes((id || "").toLowerCase()) ? "filter-panel--scroll-list-only" : undefined}
              >
                <div className="filter-panel__body-inner" key={id}>
                  {action === "employeesByDepartment" && <EmployeesByDepartmentInputs />}
                  {action === "growthByDepartment" && <GrowthByDepartmentInputs />}
                  {action === "foundedYear" && <FoundedYearInputs />}
                  {action === "companyDomainName" && (
                    <>
                      <FilterComponentAction
                        key={id}
                        {...item}
                        activeIndex={activeIndex}
                        index={activeFilterIndex}
                        setTableLoading={setTableLoading}
                        setActiveIndex={setActiveIndex}
                        filterCount={filterCount}
                      />
                      <AddRemoveValue
                        defaultValue={(() => {
                          const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
                          const f = dataSearchState?.filterComponentData || {};
                          if (st === "domainList" && (f.companyDomainList || []).length > 0) return ["Company Domain List"];
                          if (st === "nameList" && (f.companyNameList || []).length > 0) return ["Company Name List"];
                          if (st === "linkedinList" && (f.companyLinkedInList || []).length > 0) return ["LinkedIn URL List"];
                          if (st === "name") return [...new Set(f.companyName || [])];
                          if (st === "linkedin") return [...new Set(f.companyLinkedIn || [])];
                          return [...new Set(f.companyDomain || [])];
                        })()}
                        id={id}
                        listKey={(() => {
                          const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
                          if (st === "domainList") return "companyDomainList";
                          if (st === "nameList") return "companyNameList";
                          if (st === "linkedinList") return "companyLinkedInList";
                          if (st === "name") return "companyName";
                          if (st === "linkedin") return "companyLinkedIn";
                          return "companyDomain";
                        })()}
                        store={dataSearchState}
                        setTableLoading={setTableLoading}
                      />
                    </>
                  )}
                  {action === "contactInfo" && (
                    <>
                      <FilterComponentAction
                        key={id}
                        {...item}
                        activeIndex={activeIndex}
                        index={activeFilterIndex}
                        setTableLoading={setTableLoading}
                        setActiveIndex={setActiveIndex}
                        filterCount={filterCount}
                      />
                      <AddRemoveValue
                        defaultValue={(() => {
                          const st = dataSearchState?.filterComponentData?.contactSearchType || "name";
                          const f = dataSearchState?.filterComponentData || {};
                          if (st === "linkedin") {
                            return [...new Set(f.contactLinkedIn || [])];
                          }
                          const raw = f.name;
                          const arr = Array.isArray(raw)
                            ? raw
                            : typeof raw === "string" && raw.trim()
                              ? [raw.trim()]
                              : [];
                          return [...new Set(arr)];
                        })()}
                        id="name"
                        listKey={
                          (dataSearchState?.filterComponentData?.contactSearchType || "name") === "linkedin"
                            ? "contactLinkedIn"
                            : "name"
                        }
                        store={dataSearchState}
                        setTableLoading={setTableLoading}
                      />
                    </>
                  )}
                  {action === "experienceDuration" && (
                    <FilterComponentAction
                      key={id}
                      {...item}
                      activeIndex={activeIndex}
                      index={activeFilterIndex}
                      setTableLoading={setTableLoading}
                      setActiveIndex={setActiveIndex}
                      filterCount={filterCount}
                    />
                  )}
                  {action === "excludeContacts" && (
                    <ExcludeContactsInputs />
                  )}
                  {action === "inputBox" && (
                    <>
                      {id === "title" ? (
                        <TitleFilterPanel item={item} setTableLoading={setTableLoading} />
                      ) : id === "skill" ? (
                        <TextArrayFilterPanel item={item} setTableLoading={setTableLoading} includeKey="skill" excludeKey="skillExclude" placeholder="e.g. leadership, Python" />
                      ) : id === "certification" ? (
                        <TextArrayFilterPanel item={item} setTableLoading={setTableLoading} includeKey="certification" excludeKey="certificationExclude" placeholder="e.g. Scrum Master Certification (CSM)" />
                      ) : (
                        <>
                          <FilterComponentAction
                            key={id}
                            {...item}
                            activeIndex={activeIndex}
                            index={activeFilterIndex}
                            setTableLoading={setTableLoading}
                            setActiveIndex={setActiveIndex}
                            filterCount={filterCount}
                          />
                          <AddRemoveValue
                            defaultValue={[...new Set(dataSearchState?.filterComponentData?.[id])] || []}
                            id={id}
                            store={dataSearchState}
                            setTableLoading={setTableLoading}
                          />
                        </>
                      )}
                    </>
                  )}
                  {action === "toggle" && (
                    <FilterComponentAction
                      key={id}
                      {...item}
                      activeIndex={activeIndex}
                      index={activeFilterIndex}
                      setTableLoading={setTableLoading}
                      setActiveIndex={setActiveIndex}
                    />
                  )}
                  {isDropdown && (
                    <>
                      {id?.toLowerCase() === "industry" ? (
                        <IndustryFilterPanel
                          item={item}
                          activeFilterIndex={activeFilterIndex}
                          activeIndex={activeIndex}
                          setActiveIndex={setActiveIndex}
                          setTableLoading={setTableLoading}
                        />
                      ) : (id?.toLowerCase() === "companykeywords" || id?.toLowerCase() === "contactkeywords") ? (
                        <KeywordsFilterPanel
                          item={item}
                          activeFilterIndex={activeFilterIndex}
                          activeIndex={activeIndex}
                          setActiveIndex={setActiveIndex}
                          setTableLoading={setTableLoading}
                        />
                      ) : action === "jobFunctionDropdown" ? (
                        <JobFunctionAccordion />
                      ) : action === "fundingDropdown" ? (
                        <div className="fundingPanelScroller">
                          <FundingTypeAccordion />
                          <FundingRangeInputs />
                        </div>
                      ) : (
                        <FilterComponentAction
                          key={id}
                          {...item}
                          activeIndex={activeIndex}
                          index={activeFilterIndex}
                          setActiveIndex={setActiveIndex}
                          setTableLoading={setTableLoading}
                        />
                      )}
                      {action === "autoCompleteMultiSelectDropdown" && !["industry", "companykeywords", "contactkeywords"].includes(id?.toLowerCase()) && (
                        <AddRemoveValue
                          defaultValue={dataSearchState?.selectedFilteredList?.[id] || []}
                          id={id}
                          store={dataSearchState}
                          setTableLoading={setTableLoading}
                        />
                      )}
                    </>
                  )}
                </div>
              </FilterPanelOverlay>
            );
          })()}
        </div>
          );
        })()}
      </div>
      <div
        className="filterSearchBtn filterSearchBtn--centered"
        style={{ display: "flex", justifyContent: "center", alignSelf: "stretch", width: "100%", boxSizing: "border-box" }}
      >
        <div className="filterSearchBtnInner" style={{ display: "flex", justifyContent: "center" }}>
          {!dataSearchState.hasAnyFilterSelected() ? (
            <Tooltip tooltip="Please select at least one filter to search." placement="top">
              <span style={{ display: "inline-flex" }}>
                <button
                  type="button"
                  className="btn btnPrimary filterSearchBtnButton"
                  disabled
                  onClick={() => {}}
                >
                  Search
                </button>
              </span>
            </Tooltip>
          ) : (
            <button
              type="button"
              className="btn btnPrimary filterSearchBtnButton"
              onClick={async () => {
                setActiveFilterIndex(null);
                setActiveIndex(null);
                onSliderOpenChange(false);
                await runSearchForActiveTab();
              }}
            >
              Search
            </button>
          )}
        </div>
      </div>
    </section>
  );
});

export { FilterComponent };
export default FilterComponent;
